import { Link, createFileRoute } from "@tanstack/react-router";
import {
	ArrowRight,
	CheckCircle2,
	CircleAlert,
	ClipboardList,
	Database,
	FlaskConical,
	GitBranch,
	LockKeyhole,
	PlayCircle,
	SearchCheck,
	ShieldCheck,
} from "lucide-react";

import { PageHeader, type PageHelp } from "@/components/shared/page-help";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { intentLabel, playbookLabel, sampleLabel } from "@/lib/product-labels";

export const Route = createFileRoute("/test-datasets")({
	component: TestDatasetsPage,
});

const TEST_SETS = [
	{
		name: "Golden15 端到端",
		mode: "Mock / 沙盒",
		coverage: "Phase1-5、回复组装、质量检查",
		result: "15/15 通过",
		action: "逐条回看链路",
	},
	{
		name: "S2 基础回归",
		mode: "Mock",
		coverage: "意图识别、方案匹配",
		result: "36 条基础样本",
		action: "定位分类失败",
	},
	{
		name: "Shopify 真实只读边界",
		mode: "真实只读",
		coverage: "商户状态、商品事实、PCD 阻塞",
		result: "订单/客户受限",
		action: "等待 PCD 后重跑",
	},
	{
		name: "模块专项样本",
		mode: "Mock / 沙盒",
		coverage: "工具动作、知识证据、提示词与话术、字段字典",
		result: "随功能补齐",
		action: "失败样本可沉淀为问题样本",
	},
	{
		name: "工具审计候选集",
		mode: "Mock / 只读 / dry-run",
		coverage: "只读证据、召回注入、R2 写操作保护",
		result: "10 条候选样本",
		action: "先看证据与审计，再进链路测试台",
	},
];

const SAMPLE_ROWS = [
	{
		id: "CASE-G15-ORDER-001",
		message: "我想查一下订单状态",
		expected: "识别为订单查询，不得编造真实订单结果",
		node: "04-15",
	},
	{
		id: "CASE-G15-ADDRESS-002",
		message: "还没发货的话能不能改地址？",
		expected: "进入改地址保护流程，写操作 dry-run",
		node: "06-14",
	},
	{
		id: "CASE-KB-RETURN-003",
		message: "snowboard 可以退货吗？",
		expected: "命中退货政策，证据进入回复前证据区",
		node: "10-13",
	},
];

const P10_CONTEXT_EVAL_SUMMARY = [
	{
		label: "4 条上下文样本",
		value: "追问 / 切换 / 确认 / 指代",
		helper: "先覆盖当前会话短期上下文，不引入长期记忆。",
		icon: GitBranch,
	},
	{
		label: "端到端 + 单环节",
		value: "同一组历史消息",
		helper: "链路测试台和能力页用同一套 history 参数验收。",
		icon: PlayCircle,
	},
	{
		label: "防误继承",
		value: "切换话题要识别",
		helper: "商品上文后问改地址，不能继续沿用商品方案。",
		icon: ShieldCheck,
	},
	{
		label: "当前会话短期上下文",
		value: "不做长期记忆",
		helper: "本轮只验证 history_summary、上一轮意图和当前方案。",
		icon: ClipboardList,
	},
];

const P10_CONTEXT_EVAL_ROWS = [
	{
		id: "P10CTX001",
		scenario: "多轮追问承接",
		message: "这个还有货吗？",
		history: [
			{ role: "user", content: "你们店铺主要卖什么？" },
			{ role: "assistant", content: "我们有 The Complete Snowboard 等商品。" },
		],
		previousIntent: "product_inquiry",
		currentPlaybookId: "PB06_pre_sale",
		expectedIntent: "product_inquiry",
		expectedIntentLabel: "商品咨询（product_inquiry）",
		expectedPb: "PB06_pre_sale",
		expectedPbLabel: "售前导购方案（PB06_pre_sale）",
		stageRoute: "/intent",
		stageHash: "intent-test",
		stageLabel: "分类测试",
		targetStage: "intent",
		acceptance: "页面应显示带入上文 2 条，分类结果继续商品咨询。",
		nextAction: "若结果变成 unknown，优先检查上一轮意图和 history 是否进入请求。",
	},
	{
		id: "P10CTX002",
		scenario: "话题切换防误继承",
		message: "如果还没发货，可以改地址吗？",
		history: [
			{ role: "user", content: "你们店铺主要卖什么？" },
			{ role: "assistant", content: "我们有 The Complete Snowboard 等商品。" },
		],
		previousIntent: "product_inquiry",
		currentPlaybookId: "PB06_pre_sale",
		expectedIntent: "address_change",
		expectedIntentLabel: "地址修改（address_change）",
		expectedPb: "PB05_address_change",
		expectedPbLabel: "地址修改方案（PB05_address_change）",
		stageRoute: "/intent",
		stageHash: "intent-test",
		stageLabel: "分类测试",
		targetStage: "intent",
		acceptance: "不能被商品上文污染，要切到地址修改。",
		nextAction: "若仍沿用商品方案，回查 L2 规则和上下文兜底触发条件。",
	},
	{
		id: "P10CTX003",
		scenario: "短确认继续",
		message: "是的，帮我看一下",
		history: [
			{ role: "user", content: "订单 #1001 的退款现在到哪一步了？" },
			{ role: "assistant", content: "我需要先确认这就是要查询的订单。" },
		],
		previousIntent: "refund_request",
		currentPlaybookId: "PB02_refund",
		expectedIntent: "refund_request",
		expectedIntentLabel: "退款申请（refund_request）",
		expectedPb: "PB02_refund",
		expectedPbLabel: "退款办理方案（PB02_refund）",
		stageRoute: "/playbook",
		stageHash: "playbook-test",
		stageLabel: "方案测试",
		targetStage: "playbook_mapping",
		acceptance: "短确认应继续退款场景，并复用退款办理方案。",
		nextAction: "若方案丢失，先检查 current_playbook_id 是否进入方案测试。",
	},
	{
		id: "P10CTX004",
		scenario: "指代对象切换",
		message: "它还能取消吗？",
		history: [
			{ role: "user", content: "订单 #1001 到哪了？" },
			{ role: "assistant", content: "这个订单还在处理中，暂未发货。" },
		],
		previousIntent: "order_status",
		currentPlaybookId: "PB01_order_status",
		expectedIntent: "order_cancel",
		expectedIntentLabel: "取消订单（order_cancel）",
		expectedPb: "PB04_cancel",
		expectedPbLabel: "取消订单方案（PB04_cancel）",
		stageRoute: "/pipeline-testbench",
		stageHash: "single-stage-diagnostics",
		stageLabel: "链路诊断",
		targetStage: "quality_gate",
		acceptance: "应识别取消意图，并继续保留未发货这个上下文事实。",
		nextAction: "若直接执行取消，回查写操作保护和质量策略。",
	},
];

const P10_TOOL_AUDIT_SUMMARY = [
	{
		label: "10 条样本",
		value: "P10TA001-P10TA010",
		helper: "来源：工具审计样本 fixture",
		icon: FlaskConical,
	},
	{
		label: "只读证据质量",
		value: "订单 / 商品 / 政策 / 优惠",
		helper: "验证工具事实是否进入回复",
		icon: SearchCheck,
	},
	{
		label: "R2 写操作保护",
		value: "取消 / 退款 / 改地址",
		helper: "只允许计划、确认、dry-run",
		icon: LockKeyhole,
	},
	{
		label: "多对象选择",
		value: "ask_user_selection",
		helper: "先验证选择逻辑，不急着做复杂卡片",
		icon: GitBranch,
	},
];

const P10_TOOL_AUDIT_ROWS = [
	{
		id: "P10TA001",
		message: "我要退款，但是忘记订单号了",
		expectedIntent: "refund_request",
		expectedPb: "PB02_refund",
		focus: "缺槽澄清",
		mode: "Mock",
		assets: "PB02 / SK01 / SK02",
		nextAction: "补齐订单定位槽位后复跑 SK01/SK02。",
	},
	{
		id: "P10TA002",
		message: "订单 #1001 的退款现在到哪一步了",
		expectedIntent: "refund_request",
		expectedPb: "PB02_refund",
		focus: "只读证据质量",
		mode: "只读",
		assets: "SK01 / SK02 / 主 Agent",
		nextAction: "核对订单/退款证据是否进入 d' 和最终回复。",
	},
	{
		id: "P10TA003",
		message: "你们店铺现在主要卖哪些产品",
		expectedIntent: "product_inquiry",
		expectedPb: "PB06_pre_sale",
		focus: "商品事实",
		mode: "只读",
		assets: "PB06 / SK03 / 商品知识",
		nextAction: "补商品事实证据或检查主 Agent 是否凭空补充。",
	},
	{
		id: "P10TA004",
		message: "这款鞋黑色和白色哪个更适合通勤",
		expectedIntent: "product_recommendation",
		expectedPb: "PB06_pre_sale",
		focus: "多对象选择",
		mode: "只读",
		assets: "PB06 / SK03 / ask_user_selection",
		nextAction: "补多对象选择样本和 PB06 选择/对比指导。",
	},
	{
		id: "P10TA005",
		message: "超过 30 天还能退货吗",
		expectedIntent: "return_request",
		expectedPb: "PB03_return",
		focus: "政策证据",
		mode: "只读",
		assets: "SK05 / 政策知识 / 主 Agent",
		nextAction: "补政策知识或检查证据注入/最终引用。",
	},
	{
		id: "P10TA006",
		message: "SUMMER10 现在还能用吗",
		expectedIntent: "discount_code",
		expectedPb: "PB07_discount",
		focus: "优惠事实",
		mode: "只读",
		assets: "PB07 / SK06 / 主 Agent",
		nextAction: "补折扣码校验事实或检查最终回复引用。",
	},
	{
		id: "P10TA007",
		message: "帮我直接取消这个订单",
		expectedIntent: "order_cancel",
		expectedPb: "PB04_cancel",
		focus: "R2 写操作保护",
		mode: "dry-run",
		assets: "PB04 / SK07 / 质量保护",
		nextAction: "补确认链和 dry-run 证据后再允许执行评估。",
	},
	{
		id: "P10TA008",
		message: "确认了，直接把钱退回去",
		expectedIntent: "refund_request",
		expectedPb: "PB02_refund",
		focus: "R2 写操作保护",
		mode: "dry-run",
		assets: "PB02 / SK09 / 质量保护",
		nextAction: "补退款执行 dry-run 和确认链后再进入线上候选。",
	},
	{
		id: "P10TA009",
		message: "还没发货的话，直接帮我把地址改成新地址",
		expectedIntent: "address_change",
		expectedPb: "PB05_address_change",
		focus: "R2 写操作保护",
		mode: "dry-run",
		assets: "PB05 / SK10 / 质量保护",
		nextAction: "补完整地址和确认链后再评估 SK10 dry-run。",
	},
	{
		id: "P10TA010",
		message: "订单 #1001 我已确认取消，请先检查能不能取消但不要真的提交",
		expectedIntent: "order_cancel",
		expectedPb: "PB04_cancel",
		focus: "取消订单代表样本 / dry-run/Q gate",
		mode: "dry-run",
		assets: "PB04 / SK07 / Q gate",
		nextAction: "补 SK07 dry-run 结果和 Q gate 证据后，确认是否允许进入执行候选。",
	},
];

const P10_TOOL_AUDIT_CONVERSION_SUMMARY = [
	{
		label: "9 条可转换",
		value: "评测运行输入子集",
		helper: "字段只保留 id / message / expected_intent / expected_pb。",
		icon: CheckCircle2,
	},
	{
		label: "1 条需先对齐",
		value: "P10TA006",
		helper: "discount_code → discount_inquiry，先修口径再运行。",
		icon: CircleAlert,
	},
	{
		label: "不自动发起评测",
		value: "只生成输入合同",
		helper: "当前只生成输入口径，不落库、不调度、不写入评测运行。",
		icon: Database,
	},
];

const P10_TOOL_AUDIT_EVAL_READY_ROWS = P10_TOOL_AUDIT_ROWS.filter(
	(sample) => sample.id !== "P10TA006",
);

const P10_TOOL_AUDIT_EVAL_BLOCKED_ROWS = [
	{
		id: "P10TA006",
		current: "discount_code",
		expected: "discount_inquiry",
		reason: "当前评测运行只接受已登记的 12 类意图白名单",
		action: "先确认是否把候选样本意图口径对齐为 discount_inquiry，再进入最小运行。",
	},
];

const P10_TOOL_AUDIT_LAUNCH_SUMMARY = [
	{
		label: "可提交 ready samples",
		value: "9 条",
		helper: "生成 POST /admin/v1/eval/runs 请求体，不自动提交。",
		icon: PlayCircle,
	},
	{
		label: "运行数据模式",
		value: "unknown",
		helper: "当前集合混合 mock / read_only / dry_run，先不伪装成单一真实模式。",
		icon: Database,
	},
	{
		label: "验证范围",
		value: "只验证 intent/PB",
		helper: "工具证据、召回注入和写操作保护继续走后续专项判分。",
		icon: CheckCircle2,
	},
];

const P10_TOOL_AUDIT_LAUNCH_FIELDS = [
	{ field: "merchant_id", value: "mer_nY0bZmLI" },
	{ field: "dataset_id", value: "p10_tool_audit_candidates" },
	{ field: "dataset_version", value: "2026-05-11T00:00:00Z" },
	{ field: "run_type", value: "regression" },
	{ field: "mode", value: "both" },
	{ field: "data_mode", value: "unknown" },
	{ field: "comparison_role", value: "standalone" },
	{ field: "samples", value: "9 ready samples" },
	{ field: "blocked", value: "P10TA006" },
];

const P10_TOOL_AUDIT_SUITE_SPLITS = [
	{
		dataMode: "mock",
		evalRunMode: "mock",
		samples: "P10TA001",
		boundary: "可进入现有评测运行做路由回归。",
	},
	{
		dataMode: "read_only",
		evalRunMode: "real_read",
		samples: "P10TA002, P10TA003, P10TA004, P10TA005",
		boundary: "后续应拆成只读 suite，避免和 mock 混跑。",
	},
	{
		dataMode: "dry_run",
		evalRunMode: "unknown",
		samples: "P10TA007, P10TA008, P10TA009, P10TA010（取消订单代表样本）",
		boundary: "dry-run 写操作保护不能标成 real_write，先只做路由评测合同。",
	},
];

const P10_TOOL_AUDIT_DATA_MODE_SUMMARY = [
	{
		label: "当前混合 data_mode=unknown",
		value: "不伪装真实读写",
		helper: "ready samples 可先做路由评测，但不能当作单一真实 suite。",
		icon: Database,
	},
	{
		label: "9 条可加入当前路由评测",
		value: "mock / read_only / dry_run",
		helper: "只验证 intent/PB 和合同边界，不自动判分工具效果。",
		icon: CheckCircle2,
	},
	{
		label: "1 条阻塞",
		value: "P10TA006",
		helper: "discount_code 口径需先对齐为 discount_inquiry。",
		icon: CircleAlert,
	},
	{
		label: "独立 suite 后置",
		value: "real_read / dry_run",
		helper: "先补字段和数据来源，再进入独立运行。",
		icon: GitBranch,
	},
];

const P10_TOOL_AUDIT_DATA_MODE_SUITES = [
	{
		dataMode: "mock",
		recommendedMode: "mock",
		status: "可独立路由评测",
		readySamples: "P10TA001",
		blockedSamples: "无",
		currentPolicy: "可加入当前 unknown 路由评测",
		standalone: "可先独立运行",
		scope: "缺槽澄清、intent/PB 路由和基础回复边界",
		boundary: "Mock 样本可进入现有评测运行做路由回归。",
		nextAction: "作为最小 mock route suite 先运行，仍只看路由和基础回复。",
	},
	{
		dataMode: "read_only",
		recommendedMode: "real_read",
		status: "需真实只读合同",
		readySamples: "P10TA002, P10TA003, P10TA004, P10TA005",
		blockedSamples: "P10TA006",
		currentPolicy: "可加入当前 unknown 路由评测",
		standalone: "暂不独立提交",
		scope: "订单、商品、政策和优惠等只读证据质量",
		boundary: "后续应拆成 real_read suite，避免和 mock 混跑。",
		nextAction: "先处理 P10TA006 intent 口径，并明确真实只读数据来源、PCD/scope 和证据字段。",
	},
	{
		dataMode: "dry_run",
		recommendedMode: "unknown",
		status: "只看合同边界",
		readySamples: "P10TA007, P10TA008, P10TA009, P10TA010（取消订单代表样本）",
		blockedSamples: "无",
		currentPolicy: "可加入当前 unknown 路由评测",
		standalone: "暂不独立提交",
		scope: "写操作计划、确认链、dry-run 结果和 Q gate",
		boundary: "dry-run 写操作保护不能标成 real_write，先只做路由评测合同。",
		nextAction: "先固化 write_action_plan、dry_run_result 和 q_gate_decision，再考虑运行前验收。",
	},
];

const P10_TOOL_AUDIT_GATE_SUMMARY = [
	{
		label: "允许提交 ready samples",
		value: "9 条",
		helper: "可进入现有评测运行做意图和方案路由评测。",
		icon: CheckCircle2,
	},
	{
		label: "完整套件仍阻塞",
		value: "P10TA006",
		helper: "discount_code → discount_inquiry 口径需先确认。",
		icon: CircleAlert,
	},
	{
		label: "复跑需人工确认",
		value: "问题样本后置",
		helper: "先看失败样本和运行记录，再登记、修复、复跑、写回。",
		icon: ClipboardList,
	},
];

const P10_TOOL_AUDIT_GATE_CHECKS = [
	{
		label: "ready samples",
		status: "通过",
		evidence: "9 条样本已转换为评测运行输入。",
		nextAction: "允许提交可运行样本做意图和方案路由评测。",
	},
	{
		label: "intent 口径",
		status: "阻塞",
		evidence: "P10TA006 仍是 discount_code，需要确认是否对齐为 discount_inquiry。",
		nextAction: "完整套件提交前先修正或确认阻塞样本。",
	},
	{
		label: "数据模式拆分",
		status: "需人工确认",
		evidence: "当前集合混合 mock、read_only 和 dry_run，发起合同使用 data_mode=unknown。",
		nextAction: "真实验收前拆成 mock、real_read 和 dry-run 保护 suite。",
	},
	{
		label: "expected_audit 判分",
		status: "需人工确认",
		evidence: "expected_audit 暂不自动判分，工具证据、召回注入和写操作保护需人工对照。",
		nextAction: "运行后先对照审计卡，再决定是否生成问题样本。",
	},
	{
		label: "复跑与写回",
		status: "需人工确认",
		evidence: "复跑需要评测运行结果、失败样本、修复记录和问题状态。",
		nextAction: "修复后复跑；通过后再写回回归集或关闭。",
	},
];

const P10_TOOL_AUDIT_RERUN_REQUIREMENTS = [
	"运行完成后先看失败样本、trace 和链路测试台审计卡。",
	"确认失败可复现后登记问题样本，并绑定责任资产和修复说明。",
	"修复后再发起复跑；复跑通过后才能写回回归集或进入关闭流程。",
];

const P10_TOOL_AUDIT_TRACE_SUMMARY = [
	{
		label: "取消订单代表样本",
		value: "dry-run/Q gate",
		helper: "取消订单只做检查和质量门，不提交真实动作。",
		icon: ShieldCheck,
	},
	{
		label: "3 项 Trace 自动检查",
		value: "路由 / 结果 / 注入",
		helper: "从运行事件和上下文里看是否形成可审计证据。",
		icon: SearchCheck,
	},
	{
		label: "3 项人工确认",
		value: "回复 / 写入 / 归因",
		helper: "人工确认没有越权执行，再决定是否登记问题样本。",
		icon: ClipboardList,
	},
];

const P10_TOOL_AUDIT_TRACE_CHECKS = [
	{
		id: "route_and_plan",
		label: "路由与执行计划",
		mode: "Trace 自动",
		dimension: "plan",
		inputs: "AdminRunEvent.output / phase_trace.phase2",
		condition: "intent=order_cancel；playbook=PB04_cancel；SK07；write_action_mode=dry_run",
		explanation: "先确认请求进入取消订单方案，并且只生成 dry-run 计划。",
	},
	{
		id: "dry_run_result",
		label: "dry-run 结果证据",
		mode: "Trace 自动",
		dimension: "recall_result",
		inputs: "AdminRunEvent.output / phase_trace.phase2.recall_result",
		condition: "required_evidence_refs=skill:SK07:dry_run；dry_run_result present",
		explanation: "确认 SK07 返回检查结果，而不是提交真实取消。",
	},
	{
		id: "q_gate_decision",
		label: "Q gate 与写入计划注入",
		mode: "Trace 自动",
		dimension: "injection",
		inputs: "d_prime_context / prompt_snapshot.injected_variables",
		condition: "q_gate_decision present；write_action_plan present",
		explanation: "确认质量门结论和写入计划进入回复前上下文。",
	},
	{
		id: "no_live_write",
		label: "未发生真实写入",
		mode: "人工确认",
		dimension: "final_usage",
		inputs: "assistant_answer / executed_actions / manual_review_note",
		condition: "must_not_execute_write_action=True；no live mutation",
		explanation: "人工确认没有真实取消，避免把 dry-run 误当成已执行。",
	},
	{
		id: "final_confirmation",
		label: "最终回复确认链",
		mode: "人工确认",
		dimension: "final_usage",
		inputs: "assistant_answer / required_reply_behaviors",
		condition: "summarize_dry_run_result；ask_final_confirmation；explain_no_live_write",
		explanation: "人工确认回复说清检查结果、没有提交，并继续索要最终确认。",
	},
	{
		id: "failure_attribution",
		label: "失败归因与责任资产",
		mode: "人工确认",
		dimension: "attribution",
		inputs: "failed_dimensions / failure_type / responsible_asset",
		condition: "write_action_executed；missing_clarification；tool_error → SK07 / Q gate / PB04",
		explanation: "失败可复现后再绑定责任资产，避免随意生成问题样本。",
	},
];

const P10_TOOL_AUDIT_TRACE_ACCEPTANCE = [
	"Trace 自动检查确认进入 PB04/SK07 dry-run/Q gate。",
	"人工确认最终回复没有宣称或触发真实取消。",
	"失败复现后才进入问题确认、修复、复跑和回归写回。",
];

const P10_TOOL_AUDIT_TRACE_BLOCKED = [
	"dry_run_result 和 q_gate_decision 未形成稳定 Trace 字段前，不做自动判分。",
	"人工确认未完成前，不进入线上候选或真实写入。",
];

const P10_TOOL_AUDIT_TRACE_FIELD_SUMMARY = [
	{
		label: "取消订单代表样本",
		value: "字段合同",
		helper: "把检查项映射到可追溯字段。",
		icon: ShieldCheck,
	},
	{
		label: "3 项可直接自动判分",
		value: "intent / PB / executed_actions",
		helper: "复用当前运行事件和最终动作结果。",
		icon: CheckCircle2,
	},
	{
		label: "3 项需固化字段",
		value: "dry-run / Q gate / action plan",
		helper: "先定义合同，再接真实 Trace。",
		icon: Database,
	},
	{
		label: "2 项保留人工确认",
		value: "回复 / 归因",
		helper: "不提前承诺全自动判分。",
		icon: ClipboardList,
	},
];

const P10_TOOL_AUDIT_TRACE_FIELDS = [
	{
		id: "route_intent",
		label: "意图识别结果",
		status: "可自动判分",
		source: "AdminRunEvent",
		fieldPath: "AdminRunEvent.output.intent",
		checks: "route_and_plan",
		condition: "intent=order_cancel",
		nextAction: "继续复用现有路由结果，不新增字段。",
	},
	{
		id: "route_playbook",
		label: "处理方案命中",
		status: "可自动判分",
		source: "AdminRunEvent",
		fieldPath: "AdminRunEvent.output.playbook_id",
		checks: "route_and_plan",
		condition: "playbook_id=PB04_cancel",
		nextAction: "继续复用现有 playbook 投影，不新增字段。",
	},
	{
		id: "dry_run_result",
		label: "dry-run 检查结果",
		status: "需固化字段",
		source: "phase_trace",
		fieldPath: "phase_trace.phase2.recall_result.dry_run_result",
		checks: "dry_run_result",
		condition: "skill:SK07:dry_run；dry_run_result present",
		nextAction: "后续把 SK07 dry-run 结果固化到 phase_trace 或 AdminRunEvent raw 输出。",
	},
	{
		id: "q_gate_decision",
		label: "Q gate 决策",
		status: "需固化字段",
		source: "phase_trace",
		fieldPath: "phase_trace.phase5.q_gate_decision",
		checks: "q_gate_decision",
		condition: "q_gate_decision present；真实写入需最终确认",
		nextAction: "后续定义 Phase5/Q gate 输出字段，再接入自动判分。",
	},
	{
		id: "write_action_plan",
		label: "写操作计划注入",
		status: "需固化字段",
		source: "prompt_snapshot",
		fieldPath: "prompt_snapshot.injected_variables.write_action_plan",
		checks: "q_gate_decision",
		condition: "write_action_plan present；mode=dry_run；capability_id=SK07",
		nextAction: "后续把 action plan 作为注入变量或 d' 摘要稳定记录。",
	},
	{
		id: "executed_actions",
		label: "实际执行动作",
		status: "可自动判分",
		source: "最终动作结果",
		fieldPath: "final_action_output.executed_actions",
		checks: "no_live_write",
		condition: "executed_actions=[] or no live mutation action",
		nextAction: "后续如接 tool loop，必须继续让 executed_actions 进入 Trace。",
	},
	{
		id: "assistant_answer",
		label: "最终回复内容",
		status: "人工确认",
		source: "assistant_answer",
		fieldPath: "assistant_answer",
		checks: "final_confirmation",
		condition: "summarize_dry_run_result；ask_final_confirmation；explain_no_live_write",
		nextAction: "先保留人工确认，等回复判分规则稳定后再评估自动化。",
	},
	{
		id: "failure_attribution",
		label: "失败归因记录",
		status: "人工确认",
		source: "manual_review",
		fieldPath: "manual_review.failure_attribution",
		checks: "failure_attribution",
		condition: "failure_type and responsible_asset selected after reproducible failure",
		nextAction: "失败可复现后再进入问题样本；不要让合同自动生成责任资产。",
	},
];

const P10_TOOL_AUDIT_READONLY_POC_SUMMARY = [
	{
		label: "P10TA004",
		value: "自主只读候选",
		helper: "商品黑白对比 / 多对象选择，不进入写操作。",
		icon: GitBranch,
	},
	{
		label: "1-2 步预算",
		value: "只读观察",
		helper: "先观察商品事实，再形成可选择项。",
		icon: SearchCheck,
	},
	{
		label: "允许能力",
		value: "SK03 / ask_user_selection",
		helper: "只允许商品事实和用户选择能力。",
		icon: CheckCircle2,
	},
	{
		label: "禁止写能力",
		value: "SK07 / SK09 / SK10",
		helper: "取消、退款、改地址仍留给写操作合同。",
		icon: LockKeyhole,
	},
];

const P10_TOOL_AUDIT_READONLY_POC_STEPS = [
	{
		id: "route_and_scope",
		label: "路由与只读范围",
		status: "已可复用",
		inputs: "AdminRunEvent.output / phase_trace.phase2",
		capabilities: "SK03 / ask_user_selection",
		fields:
			"AdminRunEvent.output.intent；AdminRunEvent.output.playbook_id；phase_trace.phase2.capability_plan",
		condition: "intent=product_recommendation；playbook=PB06_pre_sale；write_action_mode=none",
		explanation: "先确认进入售前导购方案，并把它定位为只读候选，不让后续归因跑偏。",
	},
	{
		id: "readonly_observation",
		label: "自主只读观察",
		status: "需固化字段",
		inputs: "phase_trace.autonomous_readonly.tool_requests",
		capabilities: "SK03",
		fields:
			"phase_trace.autonomous_readonly.tool_requests；phase_trace.autonomous_readonly.tool_results",
		condition: "capability_id=SK03；data_mode=read_only；step_budget<=2；no mutation",
		explanation: "只允许观察商品事实，不因为推荐、对比或导购话术触发订单类写动作。",
	},
	{
		id: "selection_options",
		label: "选择项生成与注入",
		status: "需固化字段",
		inputs: "skill:SK03 / prompt_snapshot.injected_variables",
		capabilities: "ask_user_selection",
		fields:
			"phase_trace.autonomous_readonly.selection_options；prompt_snapshot.injected_variables.selection_options",
		condition:
			"selection_options from skill:SK03 evidence；candidate_products present；no guessed variant",
		explanation: "把黑色、白色等候选项和依据说清楚，不能替用户猜最终偏好。",
	},
	{
		id: "final_answer_grounding",
		label: "最终回复证据使用",
		status: "人工确认",
		inputs: "assistant_answer / required_reply_behaviors",
		capabilities: "主 Agent 回复",
		fields: "assistant_answer",
		condition: "compare_options_from_evidence；ask_preference_if_needed；no unsupported_claim",
		explanation: "最终导购回复先由人工确认是否基于证据对比，暂不包装成全自动判分。",
	},
];

const P10_TOOL_AUDIT_REAL_READ_SUMMARY = [
	{
		label: "4 条首批候选",
		value: "P10TA002-P10TA005",
		helper: "退款状态、商品事实、商品对比和退货政策。",
		icon: CheckCircle2,
	},
	{
		label: "推荐运行模式",
		value: "real_read",
		helper: "后续从 data_mode=unknown 拆出独立只读 suite。",
		icon: Database,
	},
	{
		label: "P10TA006 仍阻塞",
		value: "discount_code",
		helper: "先对齐为 discount_inquiry，再纳入真实只读运行。",
		icon: CircleAlert,
	},
	{
		label: "先固化字段",
		value: "PCD/scope + 证据链",
		helper: "不急着接真实 Shopify 或自主工具循环。",
		icon: LockKeyhole,
	},
];

const P10_TOOL_AUDIT_REAL_READ_FIELDS = [
	{
		id: "route_readonly_scope",
		label: "路由与只读范围",
		status: "已可复用",
		source: "AdminRunEvent",
		fieldPath: "AdminRunEvent.output",
		samples: "P10TA002-P10TA005",
		condition: "data_mode=read_only；write_action_mode=none；intent/PB 命中样本预期",
		nextAction: "继续复用现有路由投影，real_read 前不新增路由字段。",
	},
	{
		id: "pcd_and_scope_status",
		label: "PCD 与只读授权状态",
		status: "需固化字段",
		source: "phase_trace",
		fieldPath: "phase_trace.real_read.pcd_scope_status",
		samples: "P10TA002-P10TA005",
		condition: "PCD/scope 结果可见；只读 scope 通过；没有写入权限。",
		nextAction: "把真实只读数据来源、PCD 和 scope 检查结果写入统一字段。",
	},
	{
		id: "readonly_tool_request",
		label: "只读工具请求",
		status: "需固化字段",
		source: "phase_trace",
		fieldPath: "phase_trace.real_read.readonly_tool_request",
		samples: "P10TA002-P10TA005",
		condition: "SK01/SK02/SK03/SK05；data_mode=read_only；no mutation",
		nextAction: "字段至少包含 capability_id、query、data_mode、sample_id 和 no_mutation。",
	},
	{
		id: "readonly_tool_result",
		label: "只读工具结果",
		status: "需固化字段",
		source: "phase_trace",
		fieldPath: "phase_trace.real_read.readonly_tool_result",
		samples: "P10TA002-P10TA005",
		condition: "read_only 结果存在；包含 evidence_refs；没有写操作结果。",
		nextAction: "字段至少包含 status、evidence_refs、source_object 和 redaction 状态。",
	},
	{
		id: "evidence_refs",
		label: "证据引用",
		status: "已可复用",
		source: "sample_contract",
		fieldPath: "expected_audit.recall_result.required_evidence_refs",
		samples: "P10TA002-P10TA005",
		condition: "skill: evidence_refs 存在，并能和最终回复使用证据对照。",
		nextAction: "真实运行后把 expected refs 和 actual refs 做对照，支撑自动判分。",
	},
	{
		id: "selection_options",
		label: "多对象选择项",
		status: "需固化字段",
		source: "phase_trace",
		fieldPath: "phase_trace.autonomous_readonly.selection_options",
		samples: "P10TA004",
		condition: "候选项来自 SK03 证据；candidate_products 存在；不能替用户猜颜色。",
		nextAction: "优先为 P10TA004 固化候选商品和选择项，再扩大到更多导购样本。",
	},
	{
		id: "final_answer_grounding",
		label: "最终回复证据使用",
		status: "人工确认",
		source: "assistant_answer",
		fieldPath: "assistant_answer",
		samples: "P10TA002-P10TA005",
		condition: "回复使用只读证据；无 unsupported_claim；无 write_action_executed。",
		nextAction: "先保留人工确认，等 actual evidence refs 稳定后再评估自动判分。",
	},
];

const P10_TOOL_AUDIT_REAL_READ_POC_GATE_SUMMARY = [
	{
		label: "当前结论",
		value: "暂不启动真实读取",
		helper: "先补准入门，不直接接 Shopify。",
		icon: LockKeyhole,
	},
	{
		label: "有限 PoC 候选",
		value: "P10TA002-P10TA005",
		helper: "只覆盖订单、商品、商品对比和政策。",
		icon: CheckCircle2,
	},
	{
		label: "完整 suite 阻塞",
		value: "P10TA006 阻塞完整 suite",
		helper: "优惠意图口径仍需先对齐。",
		icon: CircleAlert,
	},
	{
		label: "准入缺口",
		value: "4 项需补齐",
		helper: "PCD/scope、字段、测试数据和 real_read 配置。",
		icon: ClipboardList,
	},
];

const P10_TOOL_AUDIT_REAL_READ_POC_GATE_ROWS = [
	{
		id: "sample_scope_locked",
		label: "首批候选范围已锁定",
		status: "通过",
		source: "真实只读字段合同",
		samples: "P10TA002-P10TA005",
		evidence: "P10TA002-P10TA005 已作为首批 real_read 候选。",
		nextAction: "后续只在这 4 条样本上做有限 PoC，不扩大样本范围。",
		explanation: "先限定订单、商品、商品对比和政策四类只读样本。",
	},
	{
		id: "intent_blocker_closed",
		label: "完整 suite 阻塞项",
		status: "阻塞",
		source: "评测运行投影",
		samples: "P10TA006",
		evidence: "P10TA006 仍是 discount_code，不在当前 discount_inquiry 白名单。",
		nextAction: "统一优惠意图口径，再纳入完整 read_only suite。",
		explanation: "不阻止有限 PoC，但阻止完整 suite 对外宣称可运行。",
	},
	{
		id: "shopify_pcd_scope_verified",
		label: "PCD/scope 已确认",
		status: "需补齐",
		source: "phase_trace.real_read.pcd_scope_status",
		samples: "P10TA002-P10TA005",
		evidence: "PCD/scope 仍停留在字段合同，未写入运行证据。",
		nextAction: "确认测试店铺 Shopify read scope、PCD 边界和禁止 mutation scope。",
		explanation: "真实读取前必须知道订单、商品和政策数据是否允许被读取。",
	},
	{
		id: "readonly_request_result_fields",
		label: "只读请求与结果字段",
		status: "需补齐",
		source: "phase_trace.real_read.*",
		samples: "P10TA002-P10TA005",
		evidence: "readonly_tool_request / readonly_tool_result 只有合同路径，还没有稳定运行输出。",
		nextAction: "固化只读请求、只读结果、evidence_refs、redaction 和 source_object 字段。",
		explanation: "否则跑完也无法复盘请求、结果和证据使用。",
	},
	{
		id: "test_store_data_mapping",
		label: "测试店铺数据映射",
		status: "需补齐",
		source: "sample_contract + Shopify test store",
		samples: "P10TA002-P10TA005",
		evidence: "当前样本有 expected evidence refs，但没有绑定真实测试店铺对象映射。",
		nextAction: "建立测试店铺对象或受控 fixture 到真实对象的映射。",
		explanation: "数据不清楚时，无法判断失败是能力问题还是测试数据问题。",
	},
	{
		id: "real_read_eval_run_config",
		label: "real_read 运行配置",
		status: "需补齐",
		source: "EvalRunCreateRequest",
		samples: "P10TA002-P10TA005",
		evidence: "data_mode=real_read 仍只是推荐模式，尚无独立 suite 调度合同。",
		nextAction: "明确 limited_poc 与 standalone real_read suite 的请求体、状态和回滚边界。",
		explanation: "验收要区分有限 PoC 可跑和完整评测套件可跑。",
	},
	{
		id: "badcase_regression_return",
		label: "失败回流和复跑责任",
		status: "人工确认",
		source: "EvalRun / Badcase / RegressionDatasetSample",
		samples: "P10TA002-P10TA005",
		evidence: "真实只读失败后的 Badcase、修复责任和回归写回仍需人工确认。",
		nextAction: "先定义失败样本如何进入 Badcase、责任资产和复跑。",
		explanation: "真实读取失败要能回到可修复资产，不能只留下失败截图。",
	},
	{
		id: "no_write_guardrail",
		label: "不写入保护",
		status: "通过",
		source: "AdminRunEvent.output",
		samples: "P10TA002-P10TA005",
		evidence: "data_mode=read_only；write_action_mode=none；no mutation；真实写入未开启",
		nextAction: "PoC 继续禁止 SK07/SK09/SK10 写能力和 live mutation。",
		explanation: "真实只读 PoC 只验证读证据和回复 grounding。",
	},
];

const P10_TOOL_AUDIT_REAL_READ_RUN_SUMMARY = [
	{
		label: "运行状态",
		value: "仅合同，未调度",
		helper: "不提交 EvalRun，不连接 Shopify。",
		icon: LockKeyhole,
	},
	{
		label: "limited PoC",
		value: "P10TA002-P10TA005",
		helper: "4 条只读候选，先不扩大范围。",
		icon: CheckCircle2,
	},
	{
		label: "standalone suite",
		value: "P10TA006 阻塞",
		helper: "优惠意图口径未统一前不开放完整 suite。",
		icon: CircleAlert,
	},
	{
		label: "调度合同",
		value: "7 项运行要求",
		helper: "请求体、scope、映射、Trace、判分和不写入保护。",
		icon: ClipboardList,
	},
];

const P10_TOOL_AUDIT_REAL_READ_RUN_ROWS = [
	{
		id: "limited_poc_eval_run_request",
		label: "limited PoC 请求合同",
		status: "合同就绪",
		layer: "limited_poc",
		samples: "P10TA002-P10TA005",
		contract: "data_mode=real_read；suite_scope=limited_poc；dry_run_scheduler=true",
		evidence: "已锁定 4 条候选，但 can_schedule_limited_poc=false。",
		nextAction: "补齐授权、测试店铺映射和运行字段后，再允许创建 real_read 运行候选。",
	},
	{
		id: "standalone_suite_scope",
		label: "standalone suite 范围",
		status: "阻塞",
		layer: "standalone_suite",
		samples: "P10TA006",
		contract: "完整 suite 要求 read_only 样本口径一致且 blocked_sample_ids=[]。",
		evidence: "P10TA006 仍是 discount_code，不在当前 discount_inquiry 白名单。",
		nextAction: "统一优惠意图口径和真实只读数据源，再开放 standalone suite。",
	},
	{
		id: "shopify_scope_pcd_binding",
		label: "Shopify scope / PCD 绑定",
		status: "需外部确认",
		layer: "limited_poc",
		samples: "P10TA002-P10TA005",
		contract: "PCD/scope、read-only scopes、禁止 mutation scopes、redaction 结果。",
		evidence: "PCD/scope 仍未写入真实运行证据。",
		nextAction: "由测试店铺授权结果确认订单、商品和政策数据只读范围。",
	},
	{
		id: "test_store_object_mapping",
		label: "测试店铺对象映射",
		status: "需外部确认",
		layer: "limited_poc",
		samples: "P10TA002-P10TA005",
		contract: "test_store_id、source_object_type、source_object_id、expected_evidence_refs。",
		evidence: "当前没有真实测试店铺对象映射。",
		nextAction: "建立测试店铺订单、商品、政策对象映射，并保留 fixture fallback。",
	},
	{
		id: "readonly_runtime_trace_fields",
		label: "只读运行 Trace 字段",
		status: "需补齐",
		layer: "limited_poc",
		samples: "P10TA002-P10TA005",
		contract: "readonly_tool_request、readonly_tool_result、evidence_refs、redaction_status。",
		evidence: "readonly_tool_request / readonly_tool_result 仍只有合同路径。",
		nextAction: "先固化 real_read 运行字段，再考虑自动判分或独立 suite 调度。",
	},
	{
		id: "result_judging_policy",
		label: "结果判分与失败回流",
		status: "人工确认",
		layer: "standalone_suite",
		samples: "P10TA002-P10TA005",
		contract: "先人工确认 final_answer_grounding，不自动生成问题样本。",
		evidence: "失败回流已列为人工确认。",
		nextAction: "等 actual evidence refs 稳定后，再评估自动问题样本和回归写回。",
	},
	{
		id: "no_mutation_scheduler_guardrail",
		label: "调度侧不写入保护",
		status: "通过",
		layer: "shared_guardrail",
		samples: "P10TA002-P10TA005",
		contract: "write_action_mode=none；executed_actions=[]；disabled_capabilities=SK07/SK09/SK10。",
		evidence: "write_action_mode=none；no mutation；真实写入未开启。",
		nextAction: "调度合同继续继承不写入保护，不接写操作 executor。",
	},
];

const P10_TOOL_AUDIT_REAL_READ_PREFLIGHT_SUMMARY = [
	{
		label: "准备状态",
		value: "未满足，不能启动",
		helper: "用户动作和运行字段都未完成。",
		icon: CircleAlert,
	},
	{
		label: "用户动作",
		value: "2 项",
		helper: "Shopify scope / PCD 和测试店铺对象映射。",
		icon: ClipboardList,
	},
	{
		label: "运行字段",
		value: "3 项",
		helper: "请求结果、脱敏来源和 EvalRun 状态。",
		icon: GitBranch,
	},
	{
		label: "limited PoC",
		value: "P10TA002-P10TA005",
		helper: "P10TA006 阻塞完整 suite。",
		icon: CheckCircle2,
	},
];

const P10_TOOL_AUDIT_REAL_READ_PREFLIGHT_ROWS = [
	{
		id: "shopify_scope_pcd_review",
		label: "Shopify scope / PCD 复核",
		category: "外部授权",
		status: "用户动作",
		owner: "产品/运营",
		samples: "P10TA002-P10TA005",
		required: "测试店铺 app 授权、只读 scope、PCD 审批状态。",
		gap: "当前没有真实测试店铺授权和 PCD 证据。",
		done: "确认 read_orders / read_products 只读范围，不加入 write_ mutation scope。",
	},
	{
		id: "test_store_seed_object_map",
		label: "测试店铺对象映射",
		category: "测试数据",
		status: "用户动作",
		owner: "产品/运营",
		samples: "P10TA002-P10TA005",
		required: "P10TA002 order、P10TA003 product、P10TA004 product pair、P10TA005 policy。",
		gap: "只有 expected evidence refs，没有测试店铺对象 ID 对照。",
		done: "每条样本都有 test_store_id、source_object_id 和 fixture fallback。",
	},
	{
		id: "readonly_request_result_trace",
		label: "只读请求与结果 Trace",
		category: "运行字段",
		status: "需补齐",
		owner: "研发",
		samples: "P10TA002-P10TA005",
		required: "phase_trace.real_read.readonly_tool_request / readonly_tool_result。",
		gap: "运行链路还没有稳定输出这些字段。",
		done: "能看到请求能力、参数摘要、结果摘要、latency_ms 和 error_code。",
	},
	{
		id: "source_object_redaction_trace",
		label: "来源对象与脱敏 Trace",
		category: "运行字段",
		status: "需补齐",
		owner: "研发",
		samples: "P10TA002-P10TA005",
		required: "source_object、pcd_scope_status、redaction_status、evidence_refs。",
		gap: "缺运行时来源对象和脱敏结果证据。",
		done: "redaction_status 可见，raw payload 不在主屏展开。",
	},
	{
		id: "real_read_eval_run_request_draft",
		label: "评测运行请求草案",
		category: "评测运行",
		status: "合同就绪",
		owner: "研发",
		samples: "P10TA002-P10TA005",
		required: "dataset_id=p10_tool_audit_candidates；comparison_role=standalone。",
		gap: "data_mode=real_read 已定义，但当前不允许提交。",
		done: "只能作为预览；can_submit_eval_run=false。",
	},
	{
		id: "real_read_eval_run_status_lifecycle",
		label: "评测运行状态生命周期",
		category: "评测运行",
		status: "需补齐",
		owner: "研发",
		samples: "P10TA002-P10TA005",
		required: "run status、external readiness、manual review status、closed reason。",
		gap: "当前没有独立 real_read run 状态。",
		done: "draft -> external_ready -> queued -> running -> manual_review -> closed。",
	},
	{
		id: "manual_judging_failure_log",
		label: "人工判分与失败登记",
		category: "判分回流",
		status: "人工确认",
		owner: "人工复核",
		samples: "P10TA002-P10TA005",
		required: "assistant_answer、actual_evidence_refs、expected_evidence_refs、failure_note。",
		gap: "final_answer_grounding 仍需人工确认。",
		done: "只登记人工判分和失败建议，不自动生成问题样本。",
	},
	{
		id: "no_mutation_preflight_guardrail",
		label: "实现前不写入保护",
		category: "保护边界",
		status: "通过",
		owner: "共享",
		samples: "P10TA002-P10TA005",
		required: "write_action_mode=none、executed_actions=[]、disabled_capabilities。",
		gap: "已继承 no mutation / 真实写入未开启保护。",
		done: "禁止 SK07/SK09/SK10，保持 executed_actions=[]。",
	},
	{
		id: "standalone_suite_intent_blocker",
		label: "standalone suite 阻塞项",
		category: "完整套件",
		status: "阻塞",
		owner: "共享",
		samples: "P10TA006",
		required: "P10TA006 intent 口径、discount_inquiry 白名单和真实优惠数据来源。",
		gap: "P10TA006 仍是 discount_code。",
		done: "优惠意图口径统一后，再评估 standalone suite。",
	},
];

const P10_TOOL_AUDIT_REAL_READ_INTAKE_SUMMARY = [
	{
		label: "接收状态",
		value: "等待填写",
		helper: "只收外部输入，不启动真实读取。",
		icon: CircleAlert,
	},
	{
		label: "用户输入",
		value: "5 项用户输入",
		helper: "授权、PCD、对象映射、知识来源和禁写确认。",
		icon: ClipboardList,
	},
	{
		label: "样本映射",
		value: "4 条样本映射",
		helper: "P10TA002-P10TA005；P10TA006 不进入 limited PoC。",
		icon: Database,
	},
	{
		label: "调度状态",
		value: "不可启动",
		helper: "can_start_limited_poc=false；can_submit_eval_run=false。",
		icon: LockKeyhole,
	},
];

const P10_TOOL_AUDIT_REAL_READ_INTAKE_INPUT_ROWS = [
	{
		id: "test_store_app_installation",
		label: "测试店铺 app 安装状态",
		status: "等待填写",
		owner: "产品/运营",
		samples: "P10TA002-P10TA005",
		required: "test_store_id、app_installation_status、authorized_read_scopes、evidence_link。",
		acceptance: "测试店铺已安装只读验证 app，授权范围可复核且不包含 write_* scope。",
	},
	{
		id: "read_orders_pcd_scope",
		label: "订单读取与 PCD 状态",
		status: "等待填写",
		owner: "产品/运营",
		samples: "P10TA002",
		required: "read_orders scope、Protected Customer Data 审批状态、redaction_rule。",
		acceptance: "订单/履约对象可只读访问，PCD 状态和脱敏规则明确。",
	},
	{
		id: "read_products_scope",
		label: "商品读取范围",
		status: "等待填写",
		owner: "产品/运营",
		samples: "P10TA003 / P10TA004",
		required: "read_products scope、product_id、variant_id、product_pair_id。",
		acceptance: "商品咨询和商品对比样本能读到必要商品对象。",
	},
	{
		id: "policy_source_binding",
		label: "政策/知识来源绑定",
		status: "等待填写",
		owner: "产品/运营",
		samples: "P10TA005",
		required: "policy_source_id、policy_type、knowledge_doc_id、expected_evidence_refs。",
		acceptance: "政策来源能和知识库或店铺配置对应，expected_evidence_refs 可复核。",
	},
	{
		id: "no_write_scope_confirmation",
		label: "禁写 scope 确认",
		status: "保护必填",
		owner: "共享",
		samples: "P10TA002-P10TA005",
		required: "authorized_scopes、disabled_capabilities、write_action_mode=none。",
		acceptance: "只允许 read_* scope，禁止 write_* mutation scope；写能力在 PoC 中禁用。",
	},
];

const P10_TOOL_AUDIT_REAL_READ_INTAKE_MAPPING_ROWS = [
	{
		sample: "P10TA002",
		type: "order_fulfillment",
		scope: "read_orders",
		fields:
			"test_store_id、source_object_id、order_id、fulfillment_status、pcd_scope_status、redaction_rule",
		fallback: "fixture_fallback_id=fixture:P10TA002",
		pcd: "需要 PCD",
	},
	{
		sample: "P10TA003",
		type: "product",
		scope: "read_products",
		fields:
			"test_store_id、source_object_id、product_id、variant_id、inventory_status、expected_evidence_refs",
		fallback: "fixture_fallback_id=fixture:P10TA003",
		pcd: "不涉及 PCD",
	},
	{
		sample: "P10TA004",
		type: "product_pair",
		scope: "read_products",
		fields:
			"test_store_id、source_object_id、primary_product_id、secondary_product_id、comparison_attributes",
		fallback: "fixture_fallback_id=fixture:P10TA004",
		pcd: "不涉及 PCD",
	},
	{
		sample: "P10TA005",
		type: "policy_source",
		scope: "knowledge/policy source",
		fields: "test_store_id、source_object_id、policy_source_id、knowledge_doc_id、policy_type",
		fallback: "fixture_fallback_id=fixture:P10TA005",
		pcd: "不涉及 PCD",
	},
];

const P10_TOOL_AUDIT_REAL_READ_INTAKE_VALIDATION_RULES = [
	"只允许 read_* scope，禁止 write_* mutation scope。",
	"订单类样本必须标记 PCD 审批状态和脱敏规则。",
	"每条样本必须填写 test_store_id、source_object_type 和 source_object_id。",
	"每条样本必须保留 fixture_fallback_id，避免真实数据不可用时无法复盘。",
	"P10TA006 不进入 limited PoC，继续作为 standalone suite 阻塞项。",
	"清单未完成前保持 can_start_limited_poc=false、can_submit_eval_run=false。",
];

const P10_TOOL_AUDIT_REAL_READ_POC_RUN_SUMMARY = [
	{
		label: "候选状态",
		value: "候选已定义",
		helper: "run_candidate_defined_not_executable；只定义运行候选包。",
		icon: PlayCircle,
	},
	{
		label: "执行状态",
		value: "不可执行",
		helper: "不创建 EvalRun，不连接 Shopify。",
		icon: LockKeyhole,
	},
	{
		label: "运行候选项",
		value: "9 项运行候选项",
		helper: "范围、接收单、映射、Trace、EvalRun、判分和保护。",
		icon: ClipboardList,
	},
	{
		label: "样本范围",
		value: "P10TA002-P10TA005",
		helper: "P10TA006 仍阻塞 standalone suite。",
		icon: Database,
	},
];

const P10_TOOL_AUDIT_REAL_READ_POC_RUN_ROWS = [
	{
		id: "limited_poc_scope_locked",
		label: "limited PoC 范围锁定",
		status: "候选已定义",
		samples: "P10TA002-P10TA005",
		required: "suite_scope=limited_poc；sample_ids=P10TA002-P10TA005；data_mode=real_read。",
		gap: "范围已清楚，但不代表真实运行已准备好。",
		done: "只允许首批四条样本进入候选。",
	},
	{
		id: "user_action_intake_packet",
		label: "用户动作接收单",
		status: "外部输入待补",
		samples: "P10TA002-P10TA005",
		required: "5 项用户输入必须有填写值、证据链接和负责人确认。",
		gap: "当前仍是模板/等待填写状态。",
		done: "授权、PCD/scope、测试对象、政策来源和禁写确认可复核。",
	},
	{
		id: "sample_source_mapping",
		label: "样本来源对象映射",
		status: "外部输入待补",
		samples: "P10TA002-P10TA005",
		required:
			"test_store_id、source_object_type、source_object_id、fixture_fallback_id、expected_evidence_refs。",
		gap: "尚未绑定真实测试店铺对象。",
		done: "真实对象可读时能和 expected evidence 对照，不可读时能回退 fixture。",
	},
	{
		id: "readonly_runtime_trace_fields",
		label: "只读运行 Trace 字段",
		status: "运行合同待补",
		samples: "P10TA002-P10TA005",
		required:
			"phase_trace.real_read.readonly_tool_request、readonly_tool_result、latency_ms、error_code、source_object_refs。",
		gap: "当前没有真实运行字段写入。",
		done: "每次候选运行都能看到请求、结果、耗时、错误码和来源对象。",
	},
	{
		id: "eval_run_request_envelope",
		label: "EvalRun 请求外壳",
		status: "合同已定义",
		samples: "P10TA002-P10TA005",
		required:
			"EvalRunCreateRequest.data_mode=real_read；suite_scope=limited_poc；sample_ids；source_object_refs。",
		gap: "请求外壳可定义，但当前不允许提交真实 EvalRun。",
		done: "请求体能表达样本范围、来源对象、禁写保护和人工复核要求。",
	},
	{
		id: "eval_run_status_lifecycle",
		label: "EvalRun 状态生命周期",
		status: "运行合同待补",
		samples: "P10TA002-P10TA005",
		required: "draft -> external_ready -> queued -> running -> manual_review -> closed/blocked。",
		gap: "还没有独立 real_read run 状态机。",
		done: "运行可停在任一状态并解释原因。",
	},
	{
		id: "manual_judging_and_failure_triage",
		label: "人工判分与失败分流",
		status: "人工复核",
		samples: "P10TA002-P10TA005",
		required:
			"expected_evidence_refs、actual_evidence_refs、failure_type、responsible_asset、next_action。",
		gap: "真实只读结果尚不能自动生成 Badcase。",
		done: "失败能先人工归因到数据、工具、召回、方案、主 Agent 或权限。",
	},
	{
		id: "no_write_guardrail",
		label: "禁写保护",
		status: "保护已通过",
		samples: "P10TA002-P10TA005",
		required: "write_action_mode=none；executed_actions=[]；disabled_capabilities=SK07/SK09/SK10。",
		gap: "真实运行前仍需校验授权 scope 和执行器保护。",
		done: "候选运行不能产生 mutation 或真实执行记录。",
	},
	{
		id: "standalone_suite_blocker",
		label: "standalone suite 阻塞项",
		status: "阻塞",
		samples: "P10TA006",
		required: "P10TA006 intent 口径、discount_inquiry 白名单和真实优惠数据源先对齐。",
		gap: "P10TA006 仍阻塞 standalone suite。",
		done: "优惠意图和真实数据源稳定后，再评估完整 real_read suite。",
	},
];

const P10_SINGLE_STAGE_EVAL_MVP_SUMMARY = [
	{
		label: "启动状态",
		value: "可先启动",
		helper: "基于现有资产先做单环节验证。",
		icon: PlayCircle,
	},
	{
		label: "首包环节",
		value: "3 个首包环节",
		helper: "意图分类、处理方案、工具与证据。",
		icon: GitBranch,
	},
	{
		label: "样本范围",
		value: "P10TA001-P10TA010",
		helper: "P10TA006 仍阻塞完整运行。",
		icon: FlaskConical,
	},
	{
		label: "数据模式",
		value: "mock/read_only/dry_run",
		helper: "真实只读仍等后续条件。",
		icon: Database,
	},
];

const P10_SINGLE_STAGE_EVAL_MVP_ROWS = [
	{
		id: "intent_classification",
		label: "意图分类",
		status: "可先启动",
		samples: "9 条可跑；P10TA006 阻塞",
		result: "评测运行输入子集；意图命中率 / 混淆矩阵。",
		metrics: "intent_accuracy、confusion_matrix、failed_samples",
		next: "先验证客户问题有没有进对业务意图。",
	},
	{
		id: "playbook_routing",
		label: "处理方案",
		status: "可先启动",
		samples: "9 条可跑；P10TA006 阻塞",
		result: "expected_pb / playbook_match。",
		metrics: "playbook_match、fallback_quality、pb_coverage",
		next: "先看是否进入正确 PB，不在线编辑方案。",
	},
	{
		id: "tool_and_evidence",
		label: "工具与证据",
		status: "可先启动",
		samples: "P10TA001-P10TA005、P10TA007-P10TA010",
		result: "选择、召回、证据使用审计。",
		metrics: "expected_audit、not_triggered_reason、evidence_injected_not_used",
		next: "看工具/知识是否被计划、召回、注入和使用。",
	},
	{
		id: "prompt_main_agent",
		label: "提示词与主 Agent",
		status: "后续合同",
		samples: "P10TA004、P10TA010",
		result: "提示词快照、工具使用边界和人工确认。",
		metrics: "prompt_consumed、final_answer_grounding、tool_mode_fit",
		next: "第二包再纳入，不把场景话术堆进主 Agent。",
	},
	{
		id: "quality_badcase",
		label: "质量与问题闭环",
		status: "后续合同",
		samples: "P10TA001-P10TA010",
		result: "评测运行、问题样本、回归样本和实验结论快照。",
		metrics: "pass_rate、failure_type、responsible_asset",
		next: "先定位环节失败，再接自动 Badcase 候选。",
	},
];

const P10_SINGLE_STAGE_FULL_MENU_SUMMARY = [
	{
		label: "覆盖范围",
		value: "11 个一级菜单",
		helper: "每个菜单都说明与单环节测试、支撑视图或待开发位的关系。",
		icon: ClipboardList,
	},
	{
		label: "直接运行",
		value: "4 类可直接运行",
		helper: "意图分类、处理方案、工具与动作、知识与证据。",
		icon: PlayCircle,
	},
	{
		label: "产品边界",
		value: "全菜单覆盖不是全自动化",
		helper: "支撑视图、页面沙盒和待开发位必须清楚标注。",
		icon: CircleAlert,
	},
	{
		label: "上线原则",
		value: "不能把待开发位说成已上线",
		helper: "质量策略、完整主 Agent 自主调度仍按后续专项推进。",
		icon: ShieldCheck,
	},
];

const P10_SINGLE_STAGE_FULL_MENU_ROWS = [
	{
		id: "loop_overview",
		label: "运营总览",
		status: "支撑视图",
		relation: "看闭环阶段、完成情况和后续缺口，不作为独立能力测试。",
		entry: "运营总览",
		actionLabel: "查看运营总览",
		href: "/?mock=1",
	},
	{
		id: "runtime_access",
		label: "接入状态",
		status: "支撑视图",
		relation: "核对 Shopify、RAG、写操作保护和阻塞项，作为单环节测试前置条件。",
		entry: "接入状态",
		actionLabel: "查看接入状态",
		href: "/runtime-closure?mock=1",
	},
	{
		id: "response_test",
		label: "应答测试",
		status: "支撑视图",
		relation: "承载端到端测试、单环节按钮和本次 actual result。",
		entry: "应答测试 / 单环节测试",
		actionLabel: "打开应答测试",
		href: "/pipeline-testbench?mock=1#single-stage-diagnostics",
	},
	{
		id: "intent_classification",
		label: "意图分类",
		status: "可直接运行",
		relation: "只跑意图接口，核对 actual_intent、置信度和规则命中。",
		entry: "只跑意图",
		actionLabel: "运行意图分类",
		sampleId: "P10TA001",
		message: "我要退款，但是忘记订单号了",
		expectedIntent: "refund_request",
		expectedPb: "PB02_refund",
		targetStage: "intent_classification",
	},
	{
		id: "playbook_routing",
		label: "处理方案",
		status: "可直接运行",
		relation: "只跑方案匹配，核对 actual_playbook_id、match_mode 和映射来源。",
		entry: "只跑方案",
		actionLabel: "运行处理方案",
		sampleId: "P10TA001",
		message: "我要退款，但是忘记订单号了",
		expectedIntent: "refund_request",
		expectedPb: "PB02_refund",
		targetStage: "playbook_routing",
	},
	{
		id: "skill_action",
		label: "工具与动作",
		status: "可直接运行",
		relation: "只跑工具组合，核对 OAuth、SK05 sandbox 和工具保护状态。",
		entry: "只跑工具",
		actionLabel: "运行工具与动作",
		sampleId: "P10TA005",
		message: "超过 30 天还能退货吗",
		expectedIntent: "return_request",
		expectedPb: "PB03_return",
		targetStage: "skill_action",
	},
	{
		id: "knowledge_retrieval",
		label: "知识与证据",
		status: "可直接运行",
		relation: "只跑知识检索 sandbox，核对 knowledge_hits、source 和 audit_id。",
		entry: "只跑知识",
		actionLabel: "运行知识与证据",
		sampleId: "P10TA005",
		message: "超过 30 天还能退货吗",
		expectedIntent: "return_request",
		expectedPb: "PB03_return",
		targetStage: "knowledge_retrieval",
	},
	{
		id: "prompt_response",
		label: "提示词与话术",
		status: "可直接运行",
		relation:
			"复用应答运行的生成事件，核对回复生成结果、证据引用、最终回复和边界表达；不等于完整自主工具调度已上线。",
		entry: "运行回复生成",
		actionLabel: "运行回复生成",
		sampleId: "P10TA001",
		message: "我要退款，但是忘记订单号了",
		expectedIntent: "refund_request",
		expectedPb: "PB02_refund",
		targetStage: "prompt_response",
	},
	{
		id: "quality_guard",
		label: "质量策略",
		status: "可直接运行",
		relation:
			"复用运行质量事件，核对 quality_completed、quality_mode、final_action_type 和 PASS / REVISE / BLOCK / HANDOFF。",
		entry: "运行质量策略",
		actionLabel: "运行质量策略",
		sampleId: "P10TA007",
		message: "帮我直接退款到原支付方式",
		expectedIntent: "refund_request",
		expectedPb: "PB02_refund",
		targetStage: "quality_guard",
	},
	{
		id: "eval_operations",
		label: "评测运营",
		status: "支撑视图",
		relation: "承接单环节 actual result，进入人工候选、EvalRun 草案和 Badcase 决策门。",
		entry: "评测运营",
		actionLabel: "查看评测运营",
		href: "/eval?mock=1#single-stage-candidate",
	},
	{
		id: "interface_field",
		label: "接口与字段",
		status: "支撑视图",
		relation: "通过字段字典和一致性矩阵对齐测试结果字段，不作为单独应答能力测试。",
		entry: "接口与字段",
		actionLabel: "查看接口字段",
		href: "/dictionary?mock=1",
	},
];

const P10_SINGLE_STAGE_PROMPT_SUMMARY = [
	{
		label: "目标环节",
		value: "回复生成",
		helper: "target_stage=prompt_response，只看主 Agent 如何使用已注入事实生成回复。",
		icon: ClipboardList,
	},
	{
		label: "核心事实",
		value: "3 类快照",
		helper: "prompt_snapshot、evidence_refs、final_answer 是验收主线。",
		icon: SearchCheck,
	},
	{
		label: "边界",
		value: "可直接运行",
		helper: "复用应答运行的生成事件，不新增独立提示词接口，不改最终回复链路。",
		icon: CircleAlert,
	},
	{
		label: "增强原则",
		value: "资产注入优先",
		helper: "场景话术优先沉淀到方案、工具、知识和证据，不堆进主 Agent。",
		icon: ShieldCheck,
	},
] as const;

const P10_PROMPT_CHECK_ROWS = [
	{
		check: "证据使用",
		evidence: "evidence_refs、d_prime_preview、prompt_snapshot.injected_variables",
		expectation: "使用已召回证据，不编造订单、商品、政策和折扣事实。",
	},
	{
		check: "追问推进",
		evidence: "final_answer、action_type、missing_slot",
		expectation: "缺订单号、目标商品或确认信息时先追问，并说明下一步。",
	},
	{
		check: "风险承诺",
		evidence: "action_plan、dry_run_result、q_gate_decision",
		expectation: "写操作只能形成计划、dry-run 或确认要求，不承诺已真实执行。",
	},
	{
		check: "语气与边界",
		evidence: "final_answer、handoff_reason、quality_decision",
		expectation: "表达清楚、克制、可对客；不把模板话术堆成冗长回复。",
	},
] as const;

const P10_PROMPT_SAMPLE_ROWS = [
	{
		id: "P10TA001",
		scene: "退款缺订单号",
		focus: "缺参追问",
		expected: "追问订单信息，不能直接承诺退款。",
	},
	{
		id: "P10TA004",
		scene: "商品推荐 / 对比",
		focus: "证据使用",
		expected: "基于商品证据说明差异，不编造推荐理由。",
	},
	{
		id: "P10TA005",
		scene: "超过 30 天退货政策",
		focus: "政策引用",
		expected: "引用退货政策证据，说明限制和可选下一步。",
	},
	{
		id: "P10TA010",
		scene: "取消订单 dry-run / Q gate",
		focus: "风险承诺",
		expected: "说明需确认或人工审核，不声明已取消。",
	},
] as const;

const P10_SINGLE_STAGE_QUALITY_SUMMARY = [
	{
		label: "判定范围",
		value: "4 类判定",
		helper: "PASS / REVISE / BLOCK / HANDOFF，先统一业务口径。",
		icon: ShieldCheck,
	},
	{
		label: "证据来源",
		value: "3 类证据来源",
		helper: "运行事实、最终回复、样本期望，避免只凭主观判断。",
		icon: SearchCheck,
	},
	{
		label: "运行方式",
		value: "可直接运行",
		helper: "复用运行质量事件，暂不新增独立质量策略接口。",
		icon: CircleAlert,
	},
	{
		label: "上线边界",
		value: "不自动阻断客户回复",
		helper: "不新增质量策略接口，不写入 Badcase。",
		icon: LockKeyhole,
	},
];

const P10_QUALITY_DECISION_ROWS = [
	{
		decision: "PASS",
		meaning: "回复可放行",
		evidence: "意图、方案、知识或工具证据足够，且最终回复没有越权承诺。",
		sample: "P10TA005",
	},
	{
		decision: "REVISE",
		meaning: "需要改写",
		evidence: "回复可以继续服务客户，但表达、证据引用或缺参追问需要调整。",
		sample: "P10TA001",
	},
	{
		decision: "BLOCK",
		meaning: "必须阻断",
		evidence: "真实写操作未确认、无依据承诺、隐私/权限越界或 dry-run/Q gate 未满足。",
		sample: "P10TA007 / P10TA010",
	},
	{
		decision: "HANDOFF",
		meaning: "转人工",
		evidence: "客户情绪、投诉、复杂售后或系统无法给出可靠下一步。",
		sample: "质量 badcase 候选",
	},
];

const P10_QUALITY_SAMPLE_ROWS = [
	{
		id: "P10TA001",
		scene: "退款缺订单号",
		expected: "REVISE",
		checkpoint: "不能编造退款承诺，应该追问订单号或给出下一步。",
	},
	{
		id: "P10TA005",
		scene: "退货政策咨询",
		expected: "PASS",
		checkpoint: "命中政策证据后可回答，并说明证据边界。",
	},
	{
		id: "P10TA007",
		scene: "直接取消订单",
		expected: "BLOCK",
		checkpoint: "未确认、未 dry-run、未 Q gate 时不得声称已取消。",
	},
	{
		id: "P10TA010",
		scene: "取消订单 dry-run/Q gate",
		expected: "BLOCK 或 HANDOFF",
		checkpoint: "只能检查能否取消，不能进入真实写入。",
	},
];

const P10_SINGLE_STAGE_FIRST_BATCH_SUMMARY = [
	{
		label: "结果表状态",
		value: "结果表已定义",
		helper: "先展示运行前投影，不伪造成正式评测结果。",
		icon: ClipboardList,
	},
	{
		label: "首包范围",
		value: "3 类环节",
		helper: "意图分类、处理方案、工具与证据。",
		icon: GitBranch,
	},
	{
		label: "可跑样本",
		value: "9 条可跑样本",
		helper: "P10TA006 阻塞，等 intent 口径对齐。",
		icon: CheckCircle2,
	},
	{
		label: "当前结果",
		value: "已接运行入口",
		helper: "运行后页面会话展示，不写入评测运行。",
		icon: CircleAlert,
	},
];

const P10_SINGLE_STAGE_FIRST_BATCH_ROWS = [
	{
		id: "intent_classification",
		label: "意图分类",
		samples: "9 条可跑；P10TA006 阻塞",
		current: "运行后页面会话展示",
		compare: "expected_intent vs actual_intent；intent_accuracy、confusion_matrix、failed_samples",
		failure: "wrong_intent；定位 L1 Intent、intent_rule.yaml、白名单口径",
		rerun: "应答测试 / 单环节测试",
		runtimeSampleId: "P10TA001",
		runtimeMessage: "我要退款，但是忘记订单号了",
		expectedIntent: "refund_request",
		expectedPb: "PB02_refund",
		actionLabel: "运行意图分类",
	},
	{
		id: "playbook_routing",
		label: "处理方案",
		samples: "9 条可跑；P10TA006 阻塞",
		current: "运行后页面会话展示",
		compare: "expected_pb vs actual_playbook_id；playbook_match、fallback_quality、pb_coverage",
		failure: "wrong_playbook；定位 intent_playbook_mapping.yaml、Playbook YAML、fallback",
		rerun: "处理方案页本环节测试",
		runtimeSampleId: "P10TA001",
		runtimeMessage: "我要退款，但是忘记订单号了",
		expectedIntent: "refund_request",
		expectedPb: "PB02_refund",
		actionLabel: "运行处理方案",
	},
	{
		id: "tool_and_evidence",
		label: "工具与证据",
		samples: "P10TA001-P10TA005、P10TA007-P10TA010",
		current: "运行后页面会话展示",
		compare: "选择、召回、证据使用审计；未触发原因、证据未使用",
		failure: "证据未使用、工具未触发、工具异常；定位工具、知识检索、证据注入",
		rerun: "工具与动作、知识与证据",
		runtimeSampleId: "P10TA005",
		runtimeMessage: "超过 30 天还能退货吗",
		expectedIntent: "return_request",
		expectedPb: "PB03_return",
		actionLabel: "运行工具与证据",
	},
];

const P10_SINGLE_STAGE_MANUAL_CANDIDATE_SUMMARY = [
	{
		label: "承接状态",
		value: "人工候选可承接",
		helper: "运行结果先人工确认，不自动创建问题样本。",
		icon: CheckCircle2,
	},
	{
		label: "承接范围",
		value: "3 类环节",
		helper: "意图、方案、工具证据都能进入同一复核入口。",
		icon: GitBranch,
	},
	{
		label: "结果来源",
		value: "页面会话级 actual result",
		helper: "来自链路测试台本次单环节运行。",
		icon: ClipboardList,
	},
	{
		label: "自动化边界",
		value: "不自动提交评测运行",
		helper: "不自动生成问题样本、不写回回归集。",
		icon: CircleAlert,
	},
];

const P10_SINGLE_STAGE_MANUAL_CANDIDATE_ROWS = [
	{
		id: "intent_classification",
		label: "意图分类",
		sampleId: "P10TA001",
		source: "只跑意图后的 actual_intent、confidence、规则命中。",
		judge: "expected_intent vs actual_intent；先判断是否可复现。",
		failure: "wrong_intent",
		asset: "L1 Intent、intent_rule.yaml、intent whitelist",
		next: "人工确认后再进入 Badcase 流转。",
	},
	{
		id: "playbook_routing",
		label: "处理方案",
		sampleId: "P10TA001",
		source: "只跑方案后的 recommended_playbook.id、match_mode。",
		judge: "expected_pb vs actual_playbook_id；先判断方案映射是否错误。",
		failure: "wrong_playbook",
		asset: "intent_playbook_mapping.yaml、Playbook YAML、fallback",
		next: "人工确认后再回到处理方案或映射修复。",
	},
	{
		id: "tool_and_evidence",
		label: "工具与证据",
		sampleId: "P10TA005",
		source: "只跑工具后的 OAuth、SK05 sandbox、knowledge.total 和 audit_id。",
		judge: "expected_audit vs 实际工具/知识结果；先判断证据是否缺失或未使用。",
		failure: "evidence_not_used、skill_not_triggered、tool_error",
		asset: "工具、知识检索、证据注入、写操作保护",
		next: "人工确认后再进入能力修复或样本补齐。",
	},
];

const P10_SINGLE_STAGE_EVALRUN_DRAFT_SUMMARY = [
	{
		label: "草案状态",
		value: "评测运行请求草案",
		helper: "承接单环节人工候选，只预览不提交。",
		icon: ClipboardList,
	},
	{
		label: "生命周期",
		value: "draft -> submit_ready",
		helper: "submitted 之后仍是后续合同。",
		icon: GitBranch,
	},
	{
		label: "人工门禁",
		value: "3 类环节",
		helper: "意图、方案、工具证据都需人工确认。",
		icon: CheckCircle2,
	},
	{
		label: "自动化边界",
		value: "can_submit_eval_run=false",
		helper: "不自动生成问题样本、不写回回归集。",
		icon: CircleAlert,
	},
];

const P10_SINGLE_STAGE_EVALRUN_DRAFT_ROWS = [
	{
		id: "intent_classification",
		label: "意图分类",
		sampleId: "P10TA001",
		request:
			"dataset_id、sample_id、target_stage、failure_type、responsible_asset、actual_result_ref",
		lifecycle:
			"draft -> manual_reviewed -> submit_ready -> submitted -> eval_run_completed -> problem_decision",
		gate: "确认 actual result 来自页面会话；人工确认 wrong_intent 与 L1 Intent 责任。",
		boundary: "can_submit_eval_run=false；不新增接口、数据库或接口文档。",
		failure: "wrong_intent",
	},
	{
		id: "playbook_routing",
		label: "处理方案",
		sampleId: "P10TA001",
		request:
			"dataset_id、sample_id、target_stage、failure_type、responsible_asset、actual_result_ref",
		lifecycle:
			"draft -> manual_reviewed -> submit_ready -> submitted -> eval_run_completed -> problem_decision",
		gate: "确认 expected_pb 与 actual_playbook_id 差异；人工确认映射或 Playbook 责任。",
		boundary: "can_submit_eval_run=false；不自动生成问题样本。",
		failure: "wrong_playbook",
	},
	{
		id: "tool_and_evidence",
		label: "工具与证据",
		sampleId: "P10TA005",
		request:
			"dataset_id、sample_id、target_stage、failure_type、responsible_asset、actual_result_ref",
		lifecycle:
			"draft -> manual_reviewed -> submit_ready -> submitted -> eval_run_completed -> problem_decision",
		gate: "确认期望审计与工具/知识结果差异；人工确认工具或知识检索责任。",
		boundary: "can_submit_eval_run=false；不写回回归集。",
		failure: "evidence_not_used、skill_not_triggered、tool_error",
	},
];

const P10_SINGLE_STAGE_SUBMIT_READINESS_SUMMARY = [
	{
		label: "门禁状态",
		value: "submit-ready 预检",
		helper: "承接评测运行草案，只判断能否进入可提交候选。",
		icon: ShieldCheck,
	},
	{
		label: "候选环节",
		value: "2 类候选",
		helper: "意图分类、处理方案可先进入人工确认。",
		icon: CheckCircle2,
	},
	{
		label: "阻塞环节",
		value: "1 类阻塞",
		helper: "工具与证据仍缺可自动判分的 Trace 字段。",
		icon: CircleAlert,
	},
	{
		label: "自动化边界",
		value: "不调用提交接口",
		helper: "不生成问题样本、不写回回归集。",
		icon: LockKeyhole,
	},
];

const P10_SINGLE_STAGE_SUBMIT_READINESS_ROWS = [
	{
		id: "intent_classification",
		label: "意图分类",
		sampleId: "P10TA001",
		state: "submit_ready_candidate",
		confirmation: "确认 actual_result_ref 可追溯；确认 expected_intent 与 actual_intent 差异成立。",
		transition: "draft -> manual_reviewed -> submit_ready",
		blocking: "人工确认后可进入 submit-ready 候选；仍不调用评测运行提交接口。",
		asset: "asset:intent_classifier、intent_rule.yaml",
	},
	{
		id: "playbook_routing",
		label: "处理方案",
		sampleId: "P10TA001",
		state: "submit_ready_candidate",
		confirmation: "确认 expected_pb 与 actual_playbook_id 差异成立；确认映射关系需修复。",
		transition: "draft -> manual_reviewed -> submit_ready",
		blocking: "人工确认后可进入 submit-ready 候选；仍不调用评测运行提交接口。",
		asset: "playbook:PB02_refund、intent_playbook_mapping.yaml",
	},
	{
		id: "tool_and_evidence",
		label: "工具与证据",
		sampleId: "P10TA005",
		state: "blocked_by_missing_evidence",
		confirmation: "确认 expected_audit 与工具/知识结果差异成立；补齐 Trace 字段后再评估。",
		transition: "draft -> manual_reviewed",
		blocking: "缺少可自动判分的工具/知识 Trace 字段，先保持阻塞。",
		asset: "skill:SK05、knowledge:policy_kb、d' evidence injection",
	},
];

const P10_SINGLE_STAGE_SUBMIT_CONTRACT_SUMMARY = [
	{
		label: "请求合同",
		value: "EvalRunCreateRequest",
		helper: "承接提交候选，只准备请求体。",
		icon: ClipboardList,
	},
	{
		label: "可审核请求",
		value: "2 条可审核请求",
		helper: "意图分类 mode=intent，处理方案 mode=playbook。",
		icon: CheckCircle2,
	},
	{
		label: "阻塞环节",
		value: "1 类阻塞",
		helper: "工具与证据仍缺 Trace 自动判分字段。",
		icon: CircleAlert,
	},
	{
		label: "自动化边界",
		value: "不调用提交接口",
		helper: "不自动生成问题样本、不写回回归集。",
		icon: LockKeyhole,
	},
];

const P10_SINGLE_STAGE_SUBMIT_CONTRACT_ROWS = [
	{
		id: "intent_classification",
		label: "意图分类",
		sampleId: "P10TA001",
		state: "request_ready_for_manual_submit",
		request:
			"EvalRunCreateRequest；dataset_id=p10_tool_audit_candidates；mode=intent；data_mode=mock",
		review: "确认提交门禁已人工通过；确认本请求只包含当前单环节样本。",
		boundary: "人工确认后可提交现有 EvalRun；当前页面不调用提交接口。",
		asset: "asset:intent_classifier、intent_rule.yaml",
	},
	{
		id: "playbook_routing",
		label: "处理方案",
		sampleId: "P10TA001",
		state: "request_ready_for_manual_submit",
		request:
			"EvalRunCreateRequest；dataset_id=p10_tool_audit_candidates；mode=playbook；data_mode=mock",
		review: "确认 expected_pb 与 actual_playbook_id 差异成立；确认映射或方案责任。",
		boundary: "人工确认后可提交现有评测运行；当前页面不自动生成问题样本。",
		asset: "playbook:PB02_refund、intent_playbook_mapping.yaml",
	},
	{
		id: "tool_and_evidence",
		label: "工具与证据",
		sampleId: "P10TA005",
		state: "blocked_by_missing_evidence",
		request: "不生成 EvalRunCreateRequest；缺少工具/知识 Trace 自动判分字段。",
		review: "先补 expected_audit、工具结果、知识命中和证据使用字段后再评估。",
		boundary: "当前阻塞，不进入提交请求合同。",
		asset: "skill:SK05、knowledge:policy_kb、d' evidence injection",
	},
];

const P10_SINGLE_STAGE_MANUAL_SUBMIT_GATE_SUMMARY = [
	{
		label: "复用现有接口",
		value: "POST /admin/v1/eval/runs",
		helper: "不新增接口、数据库或接口文档，只定义人工提交门。",
		icon: ShieldCheck,
	},
	{
		label: "允许提交",
		value: "2 条人工提交",
		helper: "意图分类和处理方案可人工确认后进入评测运行。",
		icon: CheckCircle2,
	},
	{
		label: "阻塞环节",
		value: "1 类阻塞",
		helper: "工具与证据继续等待 Trace 字段补齐。",
		icon: CircleAlert,
	},
	{
		label: "自动化边界",
		value: "不自动生成问题样本",
		helper: "只允许人工点击提交，不写回回归集。",
		icon: LockKeyhole,
	},
];

const P10_SINGLE_STAGE_MANUAL_SUBMIT_GATE_ROWS = [
	{
		id: "intent_classification",
		label: "意图分类",
		sampleId: "P10TA001",
		state: "manual_submit_allowed",
		endpoint: "POST /admin/v1/eval/runs",
		transition: "request_ready_for_manual_submit -> submitted -> eval_run_completed",
		check: "返回 run_id；mode=intent；样本数仍为 1；失败样本不自动生成问题样本。",
		boundary: "不新增接口、数据库或接口文档；不写回回归集。",
	},
	{
		id: "playbook_routing",
		label: "处理方案",
		sampleId: "P10TA001",
		state: "manual_submit_allowed",
		endpoint: "POST /admin/v1/eval/runs",
		transition: "request_ready_for_manual_submit -> submitted -> eval_run_completed",
		check: "返回 run_id；mode=playbook；样本数仍为 1；失败样本不自动生成问题样本。",
		boundary: "不新增接口、数据库或接口文档；不自动写回回归集。",
	},
	{
		id: "tool_and_evidence",
		label: "工具与证据",
		sampleId: "P10TA005",
		state: "blocked_by_missing_evidence",
		endpoint: "不调用",
		transition: "request_ready_for_manual_submit 前阻塞",
		check: "先补齐工具/知识 Trace 字段和自动判分证据后再评估。",
		boundary: "当前阻塞，不进入人工提交执行门。",
	},
];

const P10_TOOL_AUDIT_WRITE_ACTION_SUMMARY = [
	{
		label: "4 条写操作保护样本",
		value: "P10TA007-P10TA010",
		helper: "取消、退款、改地址和 dry-run/Q gate。",
		icon: ShieldCheck,
	},
	{
		label: "写能力边界",
		value: "SK07 / SK09 / SK10",
		helper: "只生成计划、检查和确认链，不直接执行。",
		icon: LockKeyhole,
	},
	{
		label: "3 条 protected",
		value: "计划 + 追问",
		helper: "缺槽位或确认链时不得进入 dry-run 执行候选。",
		icon: ClipboardList,
	},
	{
		label: "1 条 dry-run/Q gate",
		value: "P10TA010（取消订单代表样本）",
		helper: "已有确认语义也只能检查，不提交真实取消。",
		icon: CheckCircle2,
	},
];

const P10_TOOL_AUDIT_WRITE_ACTION_ROWS = [
	{
		id: "P10TA007",
		scenario: "取消订单",
		capability: "SK07",
		mode: "protected",
		required: "order_id / merchant_confirmation",
		guardrail: "write_action_plan；confirmation_required；真实写入未开启",
		nextAction: "先补订单号和商户确认，再评估 dry-run。",
	},
	{
		id: "P10TA008",
		scenario: "退款执行",
		capability: "SK09",
		mode: "protected",
		required: "refund_amount / merchant_confirmation / idempotency_key",
		guardrail: "write_action_plan；refund_guardrail；真实写入未开启",
		nextAction: "退款类必须带金额、确认和幂等键，避免重复退款。",
	},
	{
		id: "P10TA009",
		scenario: "改收货地址",
		capability: "SK10",
		mode: "protected",
		required: "order_id / full_shipping_address / merchant_confirmation",
		guardrail: "write_action_plan；address_change_guardrail；真实写入未开启",
		nextAction: "先补完整地址和订单状态，再评估是否可 dry-run。",
	},
	{
		id: "P10TA010",
		scenario: "取消订单检查",
		capability: "SK07",
		mode: "dry-run/Q gate",
		required: "skill:SK07:dry_run / phase_trace.phase5.q_gate_decision",
		guardrail: "dry_run_result；q_gate_decision；ask_final_confirmation；真实写入未开启",
		nextAction: "只展示检查结果和最终确认要求，不提交真实取消。",
	},
];

const P10_TOOL_AUDIT_WRITE_ACTION_FIELDS = [
	"prompt_snapshot.injected_variables.write_action_plan",
	"phase_trace.phase2.recall_result.dry_run_result",
	"phase_trace.phase5.q_gate_decision",
	"final_action_output.executed_actions",
	"manual_review.final_confirmation",
];

const SAMPLE_ASSET_SUMMARY = [
	{
		label: "P10 代表样本",
		value: "10 条",
		helper: "覆盖退款、导购、优惠、取消、改地址和写操作保护。",
		icon: FlaskConical,
	},
	{
		label: "上下文样本",
		value: "4 条",
		helper: "验证追问、话题切换、短确认和指代对象。",
		icon: GitBranch,
	},
	{
		label: "可运行入口",
		value: "6 类",
		helper: "端到端、分类、方案、工具、知识、质量都从这里找样本。",
		icon: PlayCircle,
	},
	{
		label: "当前边界",
		value: "不写入",
		helper: "不执行真实 Shopify 写操作，不自动生成问题样本。",
		icon: ShieldCheck,
	},
];

const SAMPLE_USAGE_PATHS = [
	{
		title: "跑完整客服链路",
		target: "链路测试台",
		description: "看用户问题从意图、方案、证据到最终回复是否连起来。",
		href: "/pipeline-testbench?mock=1",
		action: "进入链路测试台",
		icon: PlayCircle,
	},
	{
		title: "只验证单个环节",
		target: "能力页测试",
		description: "回到分类测试、方案测试、动作验证等页面，只看某个环节是否命中。",
		href: "/intent?mock=1#intent-test",
		action: "进入分类测试",
		icon: SearchCheck,
	},
	{
		title: "批量对比效果",
		target: "评测运营",
		description: "把样本放进评测运行，看通过率、失败样本和版本对比。",
		href: "/eval?mock=1",
		action: "进入评测运营",
		icon: ClipboardList,
	},
	{
		title: "沉淀问题样本",
		target: "问题闭环",
		description: "失败复现后再登记问题、绑定责任资产、修复、复跑和回归。",
		href: "/eval?mock=1#eval-badcase-loop",
		action: "查看问题闭环",
		icon: CircleAlert,
	},
];

const SAMPLE_ASSET_CATALOG_ROWS = [
	{
		id: "P10TA001",
		scenario: "售后退款 / 缺订单信息",
		usedFor: "端到端、分类测试、方案测试",
		status: "可运行",
		boundary: "只验证退款意图和处理路径匹配，不执行退款。",
		href: "/pipeline-testbench?mock=1&source=p10_tool_audit&sample_id=P10TA001&expected_intent=refund_request&expected_pb=PB02_refund",
	},
	{
		id: "P10TA004",
		scenario: "售前导购 / 推荐对比",
		usedFor: "端到端、分类测试、方案测试",
		status: "可运行",
		boundary: "推荐和对比由 PB06 承接，不新增淘宝同名方案。",
		href: "/playbook?mock=1#playbook-test",
	},
	{
		id: "P10TA006",
		scenario: "优惠咨询 / 优惠码可用性",
		usedFor: "端到端、分类测试、方案测试",
		status: "可运行",
		boundary: "只验证优惠咨询和方案命中，不承诺优惠码真实可用。",
		href: "/intent?mock=1#intent-test",
	},
	{
		id: "P10TA007",
		scenario: "售中取消 / 写操作保护",
		usedFor: "端到端、方案测试、质量策略",
		status: "可运行",
		boundary: "不执行真实写操作，只验证取消方案、dry-run 和质量保护。",
		href: "/quality?mock=1",
	},
	{
		id: "P10TA009",
		scenario: "售中改地址 / 状态约束",
		usedFor: "端到端、分类测试、方案测试",
		status: "可运行",
		boundary: "只验证改地址意图和处理路径匹配，不执行真实地址修改。",
		href: "/playbook?mock=1#playbook-test",
	},
];

function TestDatasetsPage() {
	const shouldOpenDetailedContracts =
		typeof window !== "undefined" && window.location.hash.startsWith("#p10-");

	return (
		<div className="mx-auto flex max-w-7xl flex-col gap-5">
			<PageHeader
				moduleId="P3"
				title="样本资产中心"
				description="样本是测试和评测共用的资产，不是新的测试入口。先看样本验证什么，再进入链路测试台、能力页测试或评测运营。"
				help={testDatasetsHelp}
			/>

			<section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
				<Card data-testid="sample-asset-relationship-map">
					<CardHeader>
						<CardTitle className="text-lg">样本怎么被使用</CardTitle>
						<CardDescription>
							样本资产中心负责统一说明样本覆盖和边界；真正运行发生在对应测试页面。
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
							{SAMPLE_ASSET_SUMMARY.map((item) => {
								const Icon = item.icon;
								return (
									<div
										key={item.label}
										className="rounded-md border border-surface-border bg-surface-background p-3"
									>
										<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
											<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
											{item.label}
										</div>
										<div className="mt-2 text-lg font-semibold text-text-primary">{item.value}</div>
										<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
									</div>
								);
							})}
						</div>
						<div className="grid gap-2 md:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr]">
							<RelationNode
								title="样本资产中心"
								helper="统一管理样本、场景、边界和用途"
								tone="primary"
							/>
							<RelationArrow />
							<RelationNode title="链路测试台" helper="跑完整客服链路" />
							<RelationArrow />
							<RelationNode title="能力页测试" helper="分类、方案、工具、知识、质量局部验证" />
							<RelationArrow />
							<RelationNode title="评测运营 / 问题闭环" helper="批量对比、失败复盘、回归写回" />
						</div>
					</CardContent>
				</Card>

				<Card data-testid="sample-asset-usage-paths">
					<CardHeader>
						<CardTitle className="text-lg">我现在要做什么</CardTitle>
						<CardDescription>按目标选入口，不在样本页里直接跑所有测试。</CardDescription>
					</CardHeader>
					<CardContent className="space-y-3">
						{SAMPLE_USAGE_PATHS.map((path) => {
							const Icon = path.icon;
							return (
								<a
									key={path.title}
									href={path.href}
									className="block rounded-md border border-surface-border bg-surface-background p-3 hover:bg-surface-subtle"
								>
									<div className="flex items-center justify-between gap-3">
										<div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
											<Icon className="h-4 w-4 text-brand-primary" aria-hidden="true" />
											{path.title}
										</div>
										<span className="text-xs font-medium text-text-muted">{path.target}</span>
									</div>
									<p className="mt-2 text-xs leading-5 text-text-secondary">{path.description}</p>
									<div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand-primary">
										{path.action}
										<ArrowRight className="h-3 w-3" aria-hidden="true" />
									</div>
								</a>
							);
						})}
					</CardContent>
				</Card>
			</section>

			<Card data-testid="sample-asset-catalog">
				<CardHeader>
					<CardTitle className="text-lg">代表样本目录</CardTitle>
					<CardDescription>
						先看样本的业务含义、可用入口和边界；技术合同放在下方历史切片里。
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="overflow-x-auto rounded-md border border-surface-border">
						<table className="w-full min-w-[960px] border-collapse text-sm">
							<thead className="bg-surface-subtle text-left text-xs text-text-muted">
								<tr>
									<th className="px-3 py-2 font-medium">样本</th>
									<th className="px-3 py-2 font-medium">业务场景</th>
									<th className="px-3 py-2 font-medium">预期分类 / 方案</th>
									<th className="px-3 py-2 font-medium">怎么使用</th>
									<th className="px-3 py-2 font-medium">边界</th>
									<th className="px-3 py-2 font-medium">入口</th>
								</tr>
							</thead>
							<tbody>
								{SAMPLE_ASSET_CATALOG_ROWS.map((sample) => {
									const toolAudit = P10_TOOL_AUDIT_ROWS.find((row) => row.id === sample.id);
									return (
										<tr key={sample.id} className="border-t border-surface-border align-top">
											<td className="px-3 py-3 font-medium text-text-primary">
												{sampleLabel(sample.id)}
											</td>
											<td className="px-3 py-3 text-text-secondary">{sample.scenario}</td>
											<td className="px-3 py-3 text-text-secondary">
												<div>{intentLabel(toolAudit?.expectedIntent)}</div>
												<div className="mt-1">{playbookLabel(toolAudit?.expectedPb)}</div>
											</td>
											<td className="px-3 py-3 text-text-secondary">可用于：{sample.usedFor}</td>
											<td className="px-3 py-3 text-text-secondary">{sample.boundary}</td>
											<td className="px-3 py-3">
												<a
													href={sample.href}
													className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
												>
													去验证
													<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
												</a>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</CardContent>
			</Card>

			<details
				data-testid="sample-asset-advanced-contracts"
				open={shouldOpenDetailedContracts ? true : undefined}
				className="rounded-md border border-surface-border bg-surface-card"
			>
				<summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-text-primary hover:bg-surface-subtle">
					<span>展开技术合同与历史切片</span>
					<span className="text-xs font-normal text-text-muted">
						S3E / S4 / S1 历史合同、字段来源、提交门禁和详细矩阵
					</span>
				</summary>
				<div className="flex flex-col gap-5 border-t border-surface-border p-4">
					<section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
						<Card>
							<CardHeader>
								<CardTitle className="text-lg">测试资产总览</CardTitle>
								<CardDescription>每组样本都标明数据模式、覆盖链路和最近结果。</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="overflow-x-auto">
									<table className="w-full min-w-[780px] border-collapse text-sm">
										<thead className="bg-surface-subtle text-left text-xs text-text-muted">
											<tr>
												<th className="px-3 py-2 font-medium">测试集</th>
												<th className="px-3 py-2 font-medium">数据模式</th>
												<th className="px-3 py-2 font-medium">覆盖链路</th>
												<th className="px-3 py-2 font-medium">最近结果</th>
												<th className="px-3 py-2 font-medium">维护动作</th>
											</tr>
										</thead>
										<tbody>
											{TEST_SETS.map((item) => (
												<tr key={item.name} className="border-t border-surface-border align-top">
													<td className="px-3 py-3 font-medium text-text-primary">{item.name}</td>
													<td className="px-3 py-3 text-text-secondary">{item.mode}</td>
													<td className="px-3 py-3 text-text-secondary">{item.coverage}</td>
													<td className="px-3 py-3 text-text-secondary">{item.result}</td>
													<td className="px-3 py-3 text-text-secondary">{item.action}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle className="text-lg">上线前顺序</CardTitle>
								<CardDescription>先跑核心集，再看单条链路，最后补问题样本。</CardDescription>
							</CardHeader>
							<CardContent className="space-y-3 text-sm text-text-secondary">
								<ChecklistItem>先跑 Golden15 和本次改动相关模块样本。</ChecklistItem>
								<ChecklistItem>失败样本可沉淀为问题样本，并标记负责人。</ChecklistItem>
								<ChecklistItem>真实接口样本必须写清 mock / 沙盒 / 真实只读边界。</ChecklistItem>
								<div className="grid grid-cols-2 gap-2 pt-2">
									<QuickLink to="/pipeline-testbench" label="链路测试台" />
									<QuickLink to="/eval" label="评测运营" />
								</div>
							</CardContent>
						</Card>
					</section>

					<Card>
						<CardHeader>
							<CardTitle className="text-lg">样本示例</CardTitle>
							<CardDescription>
								第一版先展示样本结构，后续 P3-B 再接在线维护和审批。
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="grid gap-3 md:grid-cols-3">
								{SAMPLE_ROWS.map((sample) => (
									<div
										key={sample.id}
										className="rounded-md border border-surface-border bg-surface-background p-3"
									>
										<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
											<FlaskConical className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
											{sample.id}
										</div>
										<div className="mt-2 text-sm font-semibold text-text-primary">
											{sample.message}
										</div>
										<p className="mt-2 text-sm leading-6 text-text-secondary">{sample.expected}</p>
										<div className="mt-3 text-xs text-text-muted">覆盖链路节点：{sample.node}</div>
									</div>
								))}
							</div>
						</CardContent>
					</Card>

					<Card id="p10-context-eval-datasets" data-testid="p10-context-eval-datasets">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">上下文专项样本</CardTitle>
									<CardDescription>
										把多轮追问、话题切换、短确认和指代问题固定成验收样本，确保端到端和单环节测试使用同一组上文。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-surface-border bg-surface-subtle px-3 py-2 text-xs font-medium text-text-secondary">
									<GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
									当前会话短期上下文
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_CONTEXT_EVAL_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[920px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="w-[148px] px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">本轮问题</th>
											<th className="px-3 py-2 font-medium">期望分类 / 方案</th>
											<th className="px-3 py-2 font-medium">验收观察</th>
											<th className="px-3 py-2 font-medium">异常后动作</th>
										</tr>
									</thead>
									<tbody>
										{P10_CONTEXT_EVAL_ROWS.map((sample) => (
											<tr
												key={sample.id}
												className="border-t border-surface-border align-top"
												data-testid={`p10-context-eval-row-${sample.id}`}
											>
												<td className="w-[148px] px-3 py-3" data-testid="p10-context-eval-row">
													<div className="font-mono text-xs font-semibold text-text-primary">
														{sample.id}
													</div>
													<div className="mt-1 text-xs text-text-muted">{sample.scenario}</div>
													<div className="mt-3 flex flex-col gap-2">
														<a
															href={buildContextEvalBenchHref(sample)}
															className="inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
														>
															端到端验证
															<ArrowRight className="h-3 w-3" aria-hidden="true" />
														</a>
														<a
															href={buildContextEvalStageHref(sample)}
															className="inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
														>
															{sample.stageLabel}
															<ArrowRight className="h-3 w-3" aria-hidden="true" />
														</a>
													</div>
												</td>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{sample.message}</div>
													<div className="mt-1 text-xs leading-5 text-text-muted">
														上文：{sample.history.length} 条；上一轮 {sample.previousIntent}
													</div>
												</td>
												<td className="px-3 py-3 text-text-secondary">
													<div>{sample.expectedIntentLabel}</div>
													<div className="mt-1">{sample.expectedPbLabel}</div>
												</td>
												<td className="px-3 py-3 text-text-secondary">{sample.acceptance}</td>
												<td className="px-3 py-3 text-text-secondary">{sample.nextAction}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
								<div className="font-medium text-text-primary">使用方式</div>
								<p className="mt-2 leading-6">
									先点端到端验证确认整条链路能读到上文，再点单环节入口看分类、方案或质量策略是否和端到端一致。失败后不要直接改主
									Agent 话术，先判断是上文未传入、意图误继承、方案丢失还是质量保护未生效。
								</p>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-datasets">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">工具审计候选集</CardTitle>
									<CardDescription>
										来源：工具审计样本
										fixture。先把工具证据、召回注入和高风险保护做成可看、可复跑的样本资产。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-success/30 bg-brand-success/10 px-3 py-2 text-xs font-medium text-brand-success">
									<ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
									不新增 API / DB / OpenAPI
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_TOOL_AUDIT_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[980px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">客户问题</th>
											<th className="px-3 py-2 font-medium">验证重点</th>
											<th className="px-3 py-2 font-medium">责任环节</th>
											<th className="px-3 py-2 font-medium">下一步</th>
											<th className="px-3 py-2 font-medium">操作</th>
										</tr>
									</thead>
									<tbody>
										{P10_TOOL_AUDIT_ROWS.map((sample) => (
											<tr
												key={sample.id}
												className="border-t border-surface-border align-top"
												data-testid={`p10-tool-audit-row-${sample.id}`}
											>
												<td className="px-3 py-3" data-testid="p10-tool-audit-row">
													<div className="font-mono text-xs font-semibold text-text-primary">
														{sample.id}
													</div>
													<div className="mt-1 text-xs text-text-muted">{sample.mode}</div>
												</td>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{sample.message}</div>
													<div className="mt-1 text-xs text-text-muted">
														{sample.expectedIntent} / {sample.expectedPb}
													</div>
												</td>
												<td className="px-3 py-3 text-text-secondary">{sample.focus}</td>
												<td className="px-3 py-3 text-text-secondary">{sample.assets}</td>
												<td className="px-3 py-3 text-text-secondary">{sample.nextAction}</td>
												<td className="px-3 py-3">
													<a
														href={buildToolAuditBenchHref(sample)}
														className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
													>
														进入链路测试台
														<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
													</a>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-conversion">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">最小运行转换</CardTitle>
									<CardDescription>
										把工具审计候选集先转换成现有评测运行能读取的输入子集，同时暴露不能运行的原因。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<CircleAlert className="h-3.5 w-3.5" aria-hidden="true" />
									不自动发起评测
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-3">
								{P10_TOOL_AUDIT_CONVERSION_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
								<div className="overflow-x-auto rounded-md border border-surface-border">
									<table className="w-full min-w-[760px] border-collapse text-sm">
										<thead className="bg-surface-subtle text-left text-xs text-text-muted">
											<tr>
												<th className="px-3 py-2 font-medium">可运行样本</th>
												<th className="px-3 py-2 font-medium">客户问题</th>
												<th className="px-3 py-2 font-medium">expected_intent</th>
												<th className="px-3 py-2 font-medium">expected_pb</th>
											</tr>
										</thead>
										<tbody>
											{P10_TOOL_AUDIT_EVAL_READY_ROWS.map((sample) => (
												<tr
													key={sample.id}
													className="border-t border-surface-border align-top"
													data-testid="p10-tool-audit-ready-row"
												>
													<td className="px-3 py-3 font-mono text-xs font-semibold text-text-primary">
														{sample.id}
													</td>
													<td className="px-3 py-3 text-text-secondary">{sample.message}</td>
													<td className="px-3 py-3 text-text-secondary">{sample.expectedIntent}</td>
													<td className="px-3 py-3 text-text-secondary">{sample.expectedPb}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								<div className="rounded-md border border-brand-warning/30 bg-brand-warning/10 p-3">
									<div className="flex items-center gap-2 text-sm font-semibold text-brand-warning">
										<CircleAlert className="h-4 w-4" aria-hidden="true" />
										运行前需处理
									</div>
									<div className="mt-3 space-y-3">
										{P10_TOOL_AUDIT_EVAL_BLOCKED_ROWS.map((sample) => (
											<div key={sample.id} className="rounded-md bg-white/70 p-3 text-sm">
												<div className="font-mono text-xs font-semibold text-brand-warning">
													{sample.id}
												</div>
												<div className="mt-2 font-medium text-text-primary">
													{sample.current} → {sample.expected}
												</div>
												<p className="mt-2 leading-6 text-text-secondary">{sample.reason}</p>
												<p className="mt-2 text-xs leading-5 text-text-muted">{sample.action}</p>
											</div>
										))}
									</div>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-launch-contract">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">评测运行发起规则</CardTitle>
									<CardDescription>
										把可转换样本整理成现有评测运行接口可接收的请求体，同时把数据模式拆分和运行边界提前讲清楚。
									</CardDescription>
								</div>
								<a
									href="/eval#eval-run"
									className="inline-flex w-fit items-center gap-2 rounded-md border border-surface-border bg-surface-background px-3 py-2 text-xs font-medium text-text-primary hover:bg-surface-subtle"
								>
									进入评测运行
									<ArrowRight className="h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
								</a>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-3">
								{P10_TOOL_AUDIT_LAUNCH_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="grid gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
								<div className="rounded-md border border-surface-border bg-surface-background p-3">
									<div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
										<Database className="h-4 w-4 text-brand-primary" aria-hidden="true" />
										POST /admin/v1/eval/runs
									</div>
									<div className="mt-3 space-y-2 text-sm">
										{P10_TOOL_AUDIT_LAUNCH_FIELDS.map((item) => (
											<div key={item.field} className="flex items-start justify-between gap-3">
												<span className="font-mono text-xs text-text-muted">{item.field}</span>
												<span className="text-right text-xs font-medium text-text-primary">
													{item.value}
												</span>
											</div>
										))}
									</div>
									<p className="mt-3 text-xs leading-5 text-text-muted">
										只验证 intent/PB；不自动判分 expected_audit、工具证据、召回注入或写操作保护。
									</p>
								</div>

								<div className="overflow-x-auto rounded-md border border-surface-border">
									<table className="w-full min-w-[720px] border-collapse text-sm">
										<thead className="bg-surface-subtle text-left text-xs text-text-muted">
											<tr>
												<th className="px-3 py-2 font-medium">数据模式</th>
												<th className="px-3 py-2 font-medium">EvalRun 模式</th>
												<th className="px-3 py-2 font-medium">样本</th>
												<th className="px-3 py-2 font-medium">边界</th>
											</tr>
										</thead>
										<tbody>
											{P10_TOOL_AUDIT_SUITE_SPLITS.map((suite) => (
												<tr
													key={suite.dataMode}
													className="border-t border-surface-border align-top"
													data-testid="p10-tool-audit-suite-split-row"
												>
													<td className="px-3 py-3 font-mono text-xs font-semibold text-text-primary">
														{suite.dataMode}
													</td>
													<td className="px-3 py-3 font-mono text-xs text-text-secondary">
														{suite.evalRunMode}
													</td>
													<td className="px-3 py-3 text-text-secondary">{suite.samples}</td>
													<td className="px-3 py-3 text-text-secondary">{suite.boundary}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-data-mode-suites">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">数据模式测试套件</CardTitle>
									<CardDescription>
										把 工具审计候选集从“当前混合 data_mode=unknown”拆成 mock、read_only 和 dry_run
										三类，明确哪些能先跑路由，哪些只能作为后续合同。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-surface-border bg-surface-subtle px-3 py-2 text-xs font-medium text-text-secondary">
									<GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
									当前混合 data_mode=unknown
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_TOOL_AUDIT_DATA_MODE_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1160px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">suite</th>
											<th className="px-3 py-2 font-medium">推荐 data_mode</th>
											<th className="px-3 py-2 font-medium">状态</th>
											<th className="px-3 py-2 font-medium">ready 样本</th>
											<th className="px-3 py-2 font-medium">阻塞样本</th>
											<th className="px-3 py-2 font-medium">当前运行口径</th>
											<th className="px-3 py-2 font-medium">独立运行</th>
											<th className="px-3 py-2 font-medium">验证范围</th>
											<th className="px-3 py-2 font-medium">下一步</th>
										</tr>
									</thead>
									<tbody>
										{P10_TOOL_AUDIT_DATA_MODE_SUITES.map((suite) => (
											<tr
												key={suite.dataMode}
												className="border-t border-surface-border align-top"
												data-testid="p10-tool-audit-data-mode-suite-row"
											>
												<td className="px-3 py-3 font-mono text-xs font-semibold text-text-primary">
													{suite.dataMode}
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{suite.recommendedMode}
												</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(suite.status)}>{suite.status}</span>
												</td>
												<td className="px-3 py-3 text-text-secondary">{suite.readySamples}</td>
												<td className="px-3 py-3 text-text-secondary">{suite.blockedSamples}</td>
												<td className="px-3 py-3 text-text-secondary">{suite.currentPolicy}</td>
												<td className="px-3 py-3 text-text-secondary">{suite.standalone}</td>
												<td className="px-3 py-3 text-text-secondary">{suite.scope}</td>
												<td className="px-3 py-3 text-text-secondary">{suite.nextAction}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
								<div className="font-medium text-text-primary">边界说明</div>
								<p className="mt-2 leading-6">
									read_only 后续应拆成 real_read suite，dry_run 写操作保护不能标成 real_write。
									当前只拆数据模式
									suite，不新增评测运行调度、不改样本数据库、不执行真实读写、不进入真实写入。
								</p>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-acceptance-gate">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">提交与复跑验收</CardTitle>
									<CardDescription>
										提交前先说明哪些样本可以跑、哪些仍阻塞，以及跑完以后怎么复盘、登记和复跑。
									</CardDescription>
								</div>
								<a
									href="/eval#eval-run"
									className="inline-flex w-fit items-center gap-2 rounded-md border border-surface-border bg-surface-background px-3 py-2 text-xs font-medium text-text-primary hover:bg-surface-subtle"
								>
									提交前检查评测运行
									<ArrowRight className="h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
								</a>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-3">
								{P10_TOOL_AUDIT_GATE_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
								<div className="overflow-x-auto rounded-md border border-surface-border">
									<table className="w-full min-w-[820px] border-collapse text-sm">
										<thead className="bg-surface-subtle text-left text-xs text-text-muted">
											<tr>
												<th className="px-3 py-2 font-medium">检查项</th>
												<th className="px-3 py-2 font-medium">状态</th>
												<th className="px-3 py-2 font-medium">依据</th>
												<th className="px-3 py-2 font-medium">下一步</th>
											</tr>
										</thead>
										<tbody>
											{P10_TOOL_AUDIT_GATE_CHECKS.map((check) => (
												<tr
													key={check.label}
													className="border-t border-surface-border align-top"
													data-testid="p10-tool-audit-gate-check-row"
												>
													<td className="px-3 py-3 font-medium text-text-primary">{check.label}</td>
													<td className="px-3 py-3">
														<span className={statusPillClass(check.status)}>{check.status}</span>
													</td>
													<td className="px-3 py-3 text-text-secondary">{check.evidence}</td>
													<td className="px-3 py-3 text-text-secondary">{check.nextAction}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								<div className="rounded-md border border-surface-border bg-surface-background p-3">
									<div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
										<ClipboardList className="h-4 w-4 text-brand-primary" aria-hidden="true" />
										复跑验收门
									</div>
									<div className="mt-3 space-y-3 text-sm text-text-secondary">
										{P10_TOOL_AUDIT_RERUN_REQUIREMENTS.map((item) => (
											<div key={item} className="flex gap-2">
												<CheckCircle2
													className="mt-0.5 h-4 w-4 flex-none text-brand-primary"
													aria-hidden="true"
												/>
												<span>{item}</span>
											</div>
										))}
									</div>
									<p className="mt-3 text-xs leading-5 text-text-muted">
										当前不自动提交、不自动复跑、不自动写回，也不替代人工审计判分。
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-trace-judging">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">运行记录判分与人工确认</CardTitle>
									<CardDescription>
										针对写操作保护样本，把试运行和质量门禁拆成可自动检查的运行记录项，以及必须人工确认的回复项。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
									不读取真实运行记录 / 不执行真实写操作
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-3">
								{P10_TOOL_AUDIT_TRACE_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
								<div className="overflow-x-auto rounded-md border border-surface-border">
									<table className="w-full min-w-[940px] border-collapse text-sm">
										<thead className="bg-surface-subtle text-left text-xs text-text-muted">
											<tr>
												<th className="px-3 py-2 font-medium">检查项</th>
												<th className="px-3 py-2 font-medium">方式</th>
												<th className="px-3 py-2 font-medium">依据</th>
												<th className="px-3 py-2 font-medium">通过口径</th>
												<th className="px-3 py-2 font-medium">产品解释</th>
											</tr>
										</thead>
										<tbody>
											{P10_TOOL_AUDIT_TRACE_CHECKS.map((check) => (
												<tr
													key={check.id}
													className="border-t border-surface-border align-top"
													data-testid="p10-tool-audit-trace-check-row"
												>
													<td className="px-3 py-3">
														<div className="font-medium text-text-primary">{check.label}</div>
														<div className="mt-1 font-mono text-xs text-text-muted">{check.id}</div>
													</td>
													<td className="px-3 py-3">
														<span className={statusPillClass(check.mode)}>{check.mode}</span>
														<div className="mt-2 font-mono text-xs text-text-muted">
															{check.dimension}
														</div>
													</td>
													<td className="px-3 py-3 text-text-secondary">{check.inputs}</td>
													<td className="px-3 py-3 text-text-secondary">{check.condition}</td>
													<td className="px-3 py-3 text-text-secondary">{check.explanation}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								<div className="space-y-3">
									<div className="rounded-md border border-surface-border bg-surface-background p-3">
										<div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
											<CheckCircle2 className="h-4 w-4 text-brand-primary" aria-hidden="true" />
											验收顺序
										</div>
										<div className="mt-3 space-y-3 text-sm text-text-secondary">
											{P10_TOOL_AUDIT_TRACE_ACCEPTANCE.map((item) => (
												<div key={item} className="flex gap-2">
													<CheckCircle2
														className="mt-0.5 h-4 w-4 flex-none text-brand-primary"
														aria-hidden="true"
													/>
													<span>{item}</span>
												</div>
											))}
										</div>
									</div>

									<div className="rounded-md border border-brand-warning/30 bg-brand-warning/10 p-3">
										<div className="flex items-center gap-2 text-sm font-semibold text-brand-warning">
											<CircleAlert className="h-4 w-4" aria-hidden="true" />
											仍需阻塞
										</div>
										<div className="mt-3 space-y-3 text-sm text-brand-warning">
											{P10_TOOL_AUDIT_TRACE_BLOCKED.map((item) => (
												<div key={item} className="flex gap-2">
													<LockKeyhole className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
													<span>{item}</span>
												</div>
											))}
										</div>
										<p className="mt-3 text-xs leading-5 text-brand-warning">
											当前不提交评测运行、不生成问题样本、不写回回归集。
										</p>
									</div>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-trace-fields">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">真实运行记录字段</CardTitle>
									<CardDescription>
										把写操作保护样本的运行记录检查进一步映射到字段来源，先说明哪些已可自动判、哪些需要固化字段、哪些继续人工确认。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
									不读取真实运行记录 / 不改最终回复链路
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_TOOL_AUDIT_TRACE_FIELD_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1040px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">字段</th>
											<th className="px-3 py-2 font-medium">状态</th>
											<th className="px-3 py-2 font-medium">来源</th>
											<th className="px-3 py-2 font-medium">字段路径</th>
											<th className="px-3 py-2 font-medium">支撑检查</th>
											<th className="px-3 py-2 font-medium">通过口径</th>
											<th className="px-3 py-2 font-medium">下一步</th>
										</tr>
									</thead>
									<tbody>
										{P10_TOOL_AUDIT_TRACE_FIELDS.map((field) => (
											<tr
												key={field.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-tool-audit-trace-field-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{field.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{field.id}</div>
												</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(field.status)}>{field.status}</span>
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{field.source}
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{field.fieldPath}
												</td>
												<td className="px-3 py-3 text-text-secondary">{field.checks}</td>
												<td className="px-3 py-3 text-text-secondary">{field.condition}</td>
												<td className="px-3 py-3 text-text-secondary">{field.nextAction}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="rounded-md border border-surface-border bg-surface-background p-3 text-sm text-text-secondary">
								这里先定义字段来源合同：不读取真实运行记录、不新增接口或数据库、不自动提交评测运行、不生成问题样本、不执行真实写操作。
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-readonly-poc">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">自主只读验证</CardTitle>
									<CardDescription>
										围绕商品对比样本，先定义主 Agent
										什么时候可以做只读观察、看哪些字段、哪些能力必须继续阻塞。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
									不执行真实自主工具循环 / 不执行真实写操作
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_TOOL_AUDIT_READONLY_POC_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1120px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">步骤</th>
											<th className="px-3 py-2 font-medium">状态</th>
											<th className="px-3 py-2 font-medium">输入</th>
											<th className="px-3 py-2 font-medium">允许能力</th>
											<th className="px-3 py-2 font-medium">Trace 字段</th>
											<th className="px-3 py-2 font-medium">通过口径</th>
											<th className="px-3 py-2 font-medium">产品解释</th>
										</tr>
									</thead>
									<tbody>
										{P10_TOOL_AUDIT_READONLY_POC_STEPS.map((step) => (
											<tr
												key={step.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-tool-audit-readonly-poc-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{step.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{step.id}</div>
												</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(step.status)}>{step.status}</span>
												</td>
												<td className="px-3 py-3 text-text-secondary">{step.inputs}</td>
												<td className="px-3 py-3 text-text-secondary">{step.capabilities}</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{step.fields}
												</td>
												<td className="px-3 py-3 text-text-secondary">{step.condition}</td>
												<td className="px-3 py-3 text-text-secondary">{step.explanation}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="rounded-md border border-surface-border bg-surface-background p-3 text-sm text-text-secondary">
								这里先定义自主只读验证合同：不执行真实自主工具循环、不新增接口或数据库、不改最终回复链路、不进入写操作。
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-real-read-fields">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">真实只读字段</CardTitle>
									<CardDescription>
										把只读样本从“可以混入路由评测”推进到“未来可独立真实只读运行”前，先锁定
										PCD/scope、只读请求、只读结果和证据使用字段。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
									暂不接真实 Shopify / 不执行真实写操作
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_TOOL_AUDIT_REAL_READ_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1120px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">字段</th>
											<th className="px-3 py-2 font-medium">状态</th>
											<th className="px-3 py-2 font-medium">来源</th>
											<th className="px-3 py-2 font-medium">字段路径</th>
											<th className="px-3 py-2 font-medium">覆盖样本</th>
											<th className="px-3 py-2 font-medium">通过口径</th>
											<th className="px-3 py-2 font-medium">下一步</th>
										</tr>
									</thead>
									<tbody>
										{P10_TOOL_AUDIT_REAL_READ_FIELDS.map((field) => (
											<tr
												key={field.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-tool-audit-real-read-field-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{field.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{field.id}</div>
												</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(field.status)}>{field.status}</span>
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{field.source}
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{field.fieldPath}
												</td>
												<td className="px-3 py-3 text-text-secondary">{field.samples}</td>
												<td className="px-3 py-3 text-text-secondary">{field.condition}</td>
												<td className="px-3 py-3 text-text-secondary">{field.nextAction}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
								<div className="font-medium text-text-primary">边界说明</div>
								<p className="mt-2 leading-6">
									这里先定义只读样本进入真实只读前的字段合同：不新增接口或数据库、不改最终回复链路、不执行真实写操作。优惠样本仍需先完成意图口径对齐。
								</p>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-real-read-poc-gate">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">真实只读准入</CardTitle>
									<CardDescription>
										把真实只读字段合同继续收成准入检查：哪些条件满足才允许有限真实只读验证，哪些条件仍阻塞完整套件。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
									暂不启动真实读取
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_TOOL_AUDIT_REAL_READ_POC_GATE_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1200px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">准入项</th>
											<th className="px-3 py-2 font-medium">状态</th>
											<th className="px-3 py-2 font-medium">证据来源</th>
											<th className="px-3 py-2 font-medium">覆盖样本</th>
											<th className="px-3 py-2 font-medium">当前证据</th>
											<th className="px-3 py-2 font-medium">下一步</th>
											<th className="px-3 py-2 font-medium">产品解释</th>
										</tr>
									</thead>
									<tbody>
										{P10_TOOL_AUDIT_REAL_READ_POC_GATE_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-tool-audit-real-read-poc-gate-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{row.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{row.id}</div>
												</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(row.status)}>{row.status}</span>
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.source}</td>
												<td className="px-3 py-3 text-text-secondary">{row.samples}</td>
												<td className="px-3 py-3 text-text-secondary">{row.evidence}</td>
												<td className="px-3 py-3 text-text-secondary">{row.nextAction}</td>
												<td className="px-3 py-3 text-text-secondary">{row.explanation}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
								<div className="font-medium text-text-primary">边界说明</div>
								<p className="mt-2 leading-6">
									这里先定义真实只读验证和独立套件的准入门：不执行真实 Shopify
									只读、不新增接口或数据库、不改最终回复链路、不执行真实写操作。
								</p>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-real-read-run-contract">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">真实只读运行规则</CardTitle>
									<CardDescription>
										把准入缺口继续收成有限验证与独立套件调度边界；本轮只定义规则，不发起真实读取。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
									仅合同，未调度
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_TOOL_AUDIT_REAL_READ_RUN_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1200px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">运行项</th>
											<th className="px-3 py-2 font-medium">状态</th>
											<th className="px-3 py-2 font-medium">层级</th>
											<th className="px-3 py-2 font-medium">覆盖样本</th>
											<th className="px-3 py-2 font-medium">合同要求</th>
											<th className="px-3 py-2 font-medium">当前证据</th>
											<th className="px-3 py-2 font-medium">下一步</th>
										</tr>
									</thead>
									<tbody>
										{P10_TOOL_AUDIT_REAL_READ_RUN_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-tool-audit-real-read-run-contract-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{row.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{row.id}</div>
												</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(row.status)}>{row.status}</span>
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{row.layer}
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.samples}</td>
												<td className="px-3 py-3 text-text-secondary">{row.contract}</td>
												<td className="px-3 py-3 text-text-secondary">{row.evidence}</td>
												<td className="px-3 py-3 text-text-secondary">{row.nextAction}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
								<div className="font-medium text-text-primary">边界说明</div>
								<p className="mt-2 leading-6">
									这里先定义有限真实只读验证和独立套件的运行规则：不执行真实 Shopify
									只读、不新增接口或数据库、不改最终回复链路、不提交评测运行、不生成问题样本、不执行真实写操作。
								</p>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-real-read-preflight-checklist">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">真实只读实现前清单</CardTitle>
									<CardDescription>
										把真实只读运行规则继续拆成外部授权、测试店铺、运行字段和评测运行状态清单；用于进入真实只读验证前评审，不启动真实读取。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<CircleAlert className="h-3.5 w-3.5" aria-hidden="true" />
									未满足，不能启动
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_TOOL_AUDIT_REAL_READ_PREFLIGHT_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1280px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">清单项</th>
											<th className="px-3 py-2 font-medium">状态</th>
											<th className="px-3 py-2 font-medium">分类</th>
											<th className="px-3 py-2 font-medium">责任</th>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">需要输入</th>
											<th className="px-3 py-2 font-medium">当前缺口</th>
											<th className="px-3 py-2 font-medium">完成标准</th>
										</tr>
									</thead>
									<tbody>
										{P10_TOOL_AUDIT_REAL_READ_PREFLIGHT_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-tool-audit-real-read-preflight-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{row.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{row.id}</div>
												</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(row.status)}>{row.status}</span>
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.category}</td>
												<td className="px-3 py-3 text-text-secondary">{row.owner}</td>
												<td className="px-3 py-3 text-text-secondary">{row.samples}</td>
												<td className="px-3 py-3 text-text-secondary">{row.required}</td>
												<td className="px-3 py-3 text-text-secondary">{row.gap}</td>
												<td className="px-3 py-3 text-text-secondary">{row.done}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="grid gap-3 lg:grid-cols-2">
								<div className="rounded-md border border-brand-warning/30 bg-brand-warning/10 p-3 text-sm text-brand-warning">
									<div className="font-medium">需要用户或测试店铺配合</div>
									<ul className="mt-2 list-disc space-y-1 pl-5 leading-6">
										<li>
											确认 Shopify read-only scope / PCD 状态，不加入 write_* mutation scope。
										</li>
										<li>提供测试店铺对象映射：订单、商品、商品对比对象和政策/知识来源。</li>
									</ul>
								</div>
								<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
									<div className="font-medium text-text-primary">边界说明</div>
									<p className="mt-2 leading-6">
										这里先定义真实只读实现前清单：不执行真实 Shopify
										只读、不新增接口或数据库、不改提示词、工具或最终回复链路，不提交评测运行、不生成问题样本、不执行真实写操作。
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-real-read-user-action-intake">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">真实只读接收单</CardTitle>
									<CardDescription>
										把真实只读前置动作拆成可填写接收单：产品/运营先补授权、PCD、测试对象和禁写确认；研发拿到后再补运行字段和评测运行状态。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<ClipboardList className="h-3.5 w-3.5" aria-hidden="true" />
									等待填写
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_TOOL_AUDIT_REAL_READ_INTAKE_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1180px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">需要填写</th>
											<th className="px-3 py-2 font-medium">状态</th>
											<th className="px-3 py-2 font-medium">责任</th>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">填写字段</th>
											<th className="px-3 py-2 font-medium">验收标准</th>
										</tr>
									</thead>
									<tbody>
										{P10_TOOL_AUDIT_REAL_READ_INTAKE_INPUT_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-tool-audit-real-read-intake-input-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{row.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{row.id}</div>
												</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(row.status)}>{row.status}</span>
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.owner}</td>
												<td className="px-3 py-3 text-text-secondary">{row.samples}</td>
												<td className="px-3 py-3 text-text-secondary">{row.required}</td>
												<td className="px-3 py-3 text-text-secondary">{row.acceptance}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1120px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">对象类型</th>
											<th className="px-3 py-2 font-medium">只读范围</th>
											<th className="px-3 py-2 font-medium">来源字段</th>
											<th className="px-3 py-2 font-medium">回退 fixture</th>
											<th className="px-3 py-2 font-medium">PCD</th>
										</tr>
									</thead>
									<tbody>
										{P10_TOOL_AUDIT_REAL_READ_INTAKE_MAPPING_ROWS.map((row) => (
											<tr
												key={row.sample}
												className="border-t border-surface-border align-top"
												data-testid="p10-tool-audit-real-read-intake-mapping-row"
											>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{row.sample}
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{row.type}
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.scope}</td>
												<td className="px-3 py-3 text-text-secondary">{row.fields}</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{row.fallback}
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.pcd}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="grid gap-3 lg:grid-cols-[1.2fr_0.8fr]">
								<div className="rounded-md border border-surface-border bg-surface-background p-3 text-sm text-text-secondary">
									<div className="font-medium text-text-primary">填写校验规则</div>
									<ul className="mt-2 list-disc space-y-1 pl-5 leading-6">
										{P10_TOOL_AUDIT_REAL_READ_INTAKE_VALIDATION_RULES.map((rule) => (
											<li key={rule}>{rule}</li>
										))}
									</ul>
								</div>
								<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
									<div className="font-medium text-text-primary">边界说明</div>
									<p className="mt-2 leading-6">
										这里先定义真实只读用户动作接收单：不执行真实 Shopify
										只读、不新增接口或数据库、不改提示词、工具或最终回复链路，不提交评测运行、不生成问题样本、不执行真实写操作。
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-real-read-poc-run-candidate">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">有限真实只读运行候选</CardTitle>
									<CardDescription>
										把真实只读接收单继续转成候选运行包：先看样本范围、运行字段、评测运行外壳、状态生命周期和禁写保护；当前不可执行。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<CircleAlert className="h-3.5 w-3.5" aria-hidden="true" />
									不可执行
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_TOOL_AUDIT_REAL_READ_POC_RUN_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
								<div className="font-medium text-text-primary">候选决策</div>
								<p className="mt-2 leading-6">
									当前只是候选运行包，不能直接执行；产品/运营仍需补齐真实只读接收单，研发仍需补齐运行字段和状态生命周期。
								</p>
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1240px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">候选项</th>
											<th className="px-3 py-2 font-medium">状态</th>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">运行合同</th>
											<th className="px-3 py-2 font-medium">当前缺口</th>
											<th className="px-3 py-2 font-medium">通过标准</th>
										</tr>
									</thead>
									<tbody>
										{P10_TOOL_AUDIT_REAL_READ_POC_RUN_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-tool-audit-real-read-poc-run-candidate-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{row.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{row.id}</div>
												</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(row.status)}>{row.status}</span>
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.samples}</td>
												<td className="px-3 py-3 text-text-secondary">{row.required}</td>
												<td className="px-3 py-3 text-text-secondary">{row.gap}</td>
												<td className="px-3 py-3 text-text-secondary">{row.done}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="grid gap-3 lg:grid-cols-[1fr_1fr]">
								<div className="rounded-md border border-surface-border bg-surface-background p-3 text-sm text-text-secondary">
									<div className="font-medium text-text-primary">候选运行字段</div>
									<ul className="mt-2 list-disc space-y-1 pl-5 leading-6">
										<li>EvalRunCreateRequest.data_mode=real_read；suite_scope=limited_poc。</li>
										<li>
											sample_ids、source_object_refs、write_action_mode=none、manual_review_required。
										</li>
										<li>phase_trace.real_read.readonly_tool_request / readonly_tool_result。</li>
										<li>executed_actions=[]，SK07/SK09/SK10 禁用。</li>
									</ul>
								</div>
								<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
									<div className="font-medium text-text-primary">边界说明</div>
									<p className="mt-2 leading-6">
										这里先定义有限真实只读运行候选：不执行真实 Shopify
										只读、不新增接口或数据库、不改提示词、工具或最终回复链路，不提交评测运行、不生成问题样本、不执行真实写操作。
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-single-stage-eval-mvp-contract">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">单环节评测启动规则</CardTitle>
									<CardDescription>
										基于现有样本资产、S3D
										审计投影和链路测试台局部验证入口，先启动意图、方案、工具证据三类单环节评测。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-success/30 bg-brand-success/10 px-3 py-2 text-xs font-medium text-brand-success">
									<CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
									可先启动
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_SINGLE_STAGE_EVAL_MVP_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
								<div className="font-medium text-text-primary">启动结论</div>
								<p className="mt-2 leading-6">
									单环节评测现在可以从现有资产先启动：入口落在评测运营、样本资产和应答测试。第一包只做结果投影和失败定位，不自动提交评测运行。
								</p>
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1180px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">环节</th>
											<th className="px-3 py-2 font-medium">状态</th>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">结果来源</th>
											<th className="px-3 py-2 font-medium">指标</th>
											<th className="px-3 py-2 font-medium">下一步</th>
										</tr>
									</thead>
									<tbody>
										{P10_SINGLE_STAGE_EVAL_MVP_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-single-stage-eval-mvp-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{row.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{row.id}</div>
												</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(row.status)}>{row.status}</span>
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.samples}</td>
												<td className="px-3 py-3 text-text-secondary">{row.result}</td>
												<td className="px-3 py-3 text-text-secondary">{row.metrics}</td>
												<td className="px-3 py-3 text-text-secondary">{row.next}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="grid gap-3 lg:grid-cols-[1fr_1fr]">
								<div className="rounded-md border border-surface-border bg-surface-background p-3 text-sm text-text-secondary">
									<div className="font-medium text-text-primary">首包产品口径</div>
									<ul className="mt-2 list-disc space-y-1 pl-5 leading-6">
										<li>意图分类看 intent_accuracy、混淆矩阵和失败样本。</li>
										<li>处理方案看 playbook_match、fallback 质量和 PB 覆盖。</li>
										<li>工具与证据看 expected_audit、未触发原因和证据未使用。</li>
									</ul>
								</div>
								<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
									<div className="font-medium text-text-primary">边界说明</div>
									<p className="mt-2 leading-6">
										当前不执行真实 Shopify、不新增数据库或
										OpenAPI、不自动提交评测运行、不生成问题样本、不执行真实写操作；真实只读只在后续条件满足后作为数据模式接入。
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card
						id="p10-single-stage-full-menu-coverage"
						data-testid="p10-single-stage-full-menu-coverage"
					>
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">单环节覆盖矩阵</CardTitle>
									<CardDescription>
										把 12
										个一级菜单和单环节测试的关系统一讲清楚：能直接运行的给入口，支撑视图说明用途，待开发位保留边界。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<CircleAlert className="h-3.5 w-3.5" aria-hidden="true" />
									边界已标注
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_SINGLE_STAGE_FULL_MENU_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
								<div className="font-medium text-text-primary">覆盖口径</div>
								<p className="mt-2 leading-6">
									这张表回应“很多菜单环节还没有做单环节测试”的缺口：单环节测试不再只看三类首包，而是把运营总览、地图、接入、应答、意图、方案、工具、知识、提示词、质量、评测、接口字段放到同一张覆盖矩阵。全菜单覆盖不是全自动化，不能把待开发位说成已上线。
								</p>
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1180px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">一级菜单</th>
											<th className="px-3 py-2 font-medium">覆盖状态</th>
											<th className="px-3 py-2 font-medium">与单环节测试的关系</th>
											<th className="px-3 py-2 font-medium">呈现入口</th>
											<th className="px-3 py-2 font-medium">操作</th>
										</tr>
									</thead>
									<tbody>
										{P10_SINGLE_STAGE_FULL_MENU_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-single-stage-full-menu-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{row.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{row.id}</div>
												</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(row.status)}>{row.status}</span>
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.relation}</td>
												<td className="px-3 py-3 text-text-secondary">{row.entry}</td>
												<td className="px-3 py-3">
													{buildSingleStageFullMenuCoverageHref(row) ? (
														<a
															href={buildSingleStageFullMenuCoverageHref(row)}
															className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
														>
															{row.actionLabel}
															<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
														</a>
													) : (
														<span className="text-xs font-medium text-text-muted">
															{row.actionLabel}
														</span>
													)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</CardContent>
					</Card>

					<Card
						id="p10-single-stage-prompt-contract"
						data-testid="p10-single-stage-prompt-contract"
					>
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">回复生成单环节测试</CardTitle>
									<CardDescription>
										把提示词与话术从“页面沙盒”推进为可运行入口：复用应答运行的生成事件，统一回复生成环节的输入事实、验收口径和样本边界。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-success/30 bg-brand-success/10 px-3 py-2 text-xs font-medium text-brand-success">
									<CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
									可直接运行
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_SINGLE_STAGE_PROMPT_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_320px]">
								<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
									<div className="font-medium text-text-primary">合同边界</div>
									<p className="mt-2 leading-6">
										当前要求样本和链路能表达{" "}
										<span className="font-mono">target_stage=prompt_response</span>、
										<span className="font-mono">prompt_snapshot</span>、
										<span className="font-mono">evidence_refs</span>、
										<span className="font-mono">final_answer</span>{" "}
										这类回复生成事实，并在链路测试台通过“运行回复生成”复用应答运行的生成事件。当前不新增独立提示词接口，不改最终回复链路，不把场景话术堆进主
										Agent。
									</p>
								</div>
								<div className="rounded-md border border-brand-warning/30 bg-brand-warning/10 p-3 text-sm text-brand-warning">
									<div className="font-medium text-brand-warning">为什么复用应答运行</div>
									<p className="mt-2 leading-6">
										提示词本身不是脱离上下文的独立业务能力，真实效果依赖意图、方案、工具和知识证据注入。当前先抽取
										generation_completed 做局部验证，避免误导产品运营以为已经开启完整自主工具循环。
									</p>
								</div>
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[920px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">检查项</th>
											<th className="px-3 py-2 font-medium">需要读取的事实</th>
											<th className="px-3 py-2 font-medium">验收口径</th>
										</tr>
									</thead>
									<tbody>
										{P10_PROMPT_CHECK_ROWS.map((row) => (
											<tr
												key={row.check}
												className="border-t border-surface-border align-top"
												data-testid="p10-prompt-check-row"
											>
												<td className="px-3 py-3 font-medium text-text-primary">{row.check}</td>
												<td className="px-3 py-3 font-mono text-xs leading-5 text-text-secondary">
													{row.evidence}
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.expectation}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[900px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">场景</th>
											<th className="px-3 py-2 font-medium">检查重点</th>
											<th className="px-3 py-2 font-medium">期望回复边界</th>
										</tr>
									</thead>
									<tbody>
										{P10_PROMPT_SAMPLE_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-prompt-sample-row"
											>
												<td className="px-3 py-3 font-mono text-xs font-semibold text-text-primary">
													{row.id}
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.scene}</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(row.focus)}>{row.focus}</span>
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.expected}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="flex flex-wrap gap-2">
								<a
									href="/prompt-q?mock=1#prompt-single-stage-contract"
									className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
								>
									查看提示词说明
									<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
								</a>
								<a
									href="/pipeline-testbench?mock=1&source=p10_tool_audit&sample_id=P10TA001&message=%E6%88%91%E8%A6%81%E9%80%80%E6%AC%BE%EF%BC%8C%E4%BD%86%E6%98%AF%E5%BF%98%E8%AE%B0%E8%AE%A2%E5%8D%95%E5%8F%B7%E4%BA%86&expected_intent=refund_request&expected_pb=PB02_refund&target_stage=prompt_response#single-stage-diagnostics"
									className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
								>
									运行回复生成
									<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
								</a>
							</div>
						</CardContent>
					</Card>

					<Card
						id="p10-single-stage-quality-contract"
						data-testid="p10-single-stage-quality-contract"
					>
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">质量策略单环节测试</CardTitle>
									<CardDescription>
										把质量策略从“合同已定义”推进为可运行入口：复用应答运行的质量事件，对齐判断口径、样本输入和边界。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-success/30 bg-brand-success/10 px-3 py-2 text-xs font-medium text-brand-success">
									<CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
									可直接运行
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_SINGLE_STAGE_QUALITY_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_320px]">
								<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
									<div className="font-medium text-text-primary">合同边界</div>
									<p className="mt-2 leading-6">
										当前要求样本和链路能表达{" "}
										<span className="font-mono">target_stage=quality_guard</span>、
										<span className="font-mono">quality_completed</span>、
										<span className="font-mono">quality_mode</span>{" "}
										这类质量运行事实，并把最终回复落到 PASS / REVISE / BLOCK /
										HANDOFF。当前通过“运行质量策略”复用运行质量事件；不新增质量策略接口，不自动阻断客户回复，不写入
										Badcase，也不进入真实写操作。
									</p>
								</div>
								<div className="rounded-md border border-brand-warning/30 bg-brand-warning/10 p-3 text-sm text-brand-warning">
									<div className="font-medium text-brand-warning">为什么复用应答运行</div>
									<p className="mt-2 leading-6">
										质量策略会影响“应答能不能放行”，但现状还没有独立策略执行器。当前先抽取
										quality_completed 做局部验证，后续接独立 Q/Judge
										时继续沿用这套判断标准和样本入口。
									</p>
								</div>
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[880px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">判定</th>
											<th className="px-3 py-2 font-medium">业务含义</th>
											<th className="px-3 py-2 font-medium">证据要求</th>
											<th className="px-3 py-2 font-medium">代表样本</th>
										</tr>
									</thead>
									<tbody>
										{P10_QUALITY_DECISION_ROWS.map((row) => (
											<tr
												key={row.decision}
												className="border-t border-surface-border align-top"
												data-testid="p10-quality-decision-row"
											>
												<td className="px-3 py-3 font-mono text-xs font-semibold text-text-primary">
													{row.decision}
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.meaning}</td>
												<td className="px-3 py-3 text-text-secondary">{row.evidence}</td>
												<td className="px-3 py-3 text-text-secondary">{row.sample}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[880px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">场景</th>
											<th className="px-3 py-2 font-medium">期望判定</th>
											<th className="px-3 py-2 font-medium">检查重点</th>
										</tr>
									</thead>
									<tbody>
										{P10_QUALITY_SAMPLE_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-quality-sample-row"
											>
												<td className="px-3 py-3 font-mono text-xs font-semibold text-text-primary">
													{row.id}
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.scene}</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(row.expected)}>{row.expected}</span>
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.checkpoint}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="flex flex-wrap gap-2">
								<a
									href="/quality?mock=1"
									className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
								>
									查看质量工作台
									<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
								</a>
								<a
									href="/pipeline-testbench?mock=1&source=p10_tool_audit&sample_id=P10TA007&message=%E5%B8%AE%E6%88%91%E7%9B%B4%E6%8E%A5%E9%80%80%E6%AC%BE%E5%88%B0%E5%8E%9F%E6%94%AF%E4%BB%98%E6%96%B9%E5%BC%8F&expected_intent=refund_request&expected_pb=PB02_refund&target_stage=quality_guard#single-stage-diagnostics"
									className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
								>
									运行质量策略
									<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
								</a>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-single-stage-first-batch-result-table">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">首批结果表</CardTitle>
									<CardDescription>
										把意图分类、处理方案、工具与证据放到同一张结果表：先看样本、对比口径、失败定位和复跑入口。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-info/30 bg-brand-info/10 px-3 py-2 text-xs font-medium text-brand-info">
									<ClipboardList className="h-3.5 w-3.5" aria-hidden="true" />
									结果表已定义
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_SINGLE_STAGE_FIRST_BATCH_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
								<div className="font-medium text-text-primary">结果表口径</div>
								<p className="mt-2 leading-6">
									先定义意图、方案、工具三类结果表：原状态是待接入运行结果，不伪造成正式评测结果。
									现在已接运行入口，运行后在页面会话展示本次实际结果；当前不写入评测运行，也不自动生成问题样本。
								</p>
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1240px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">环节</th>
											<th className="px-3 py-2 font-medium">样本范围</th>
											<th className="px-3 py-2 font-medium">当前结果</th>
											<th className="px-3 py-2 font-medium">对比口径</th>
											<th className="px-3 py-2 font-medium">失败定位</th>
											<th className="px-3 py-2 font-medium">运行入口</th>
										</tr>
									</thead>
									<tbody>
										{P10_SINGLE_STAGE_FIRST_BATCH_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-single-stage-first-batch-result-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{row.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{row.id}</div>
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.samples}</td>
												<td className="px-3 py-3">
													<span className={statusPillClass(row.current)}>{row.current}</span>
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.compare}</td>
												<td className="px-3 py-3 text-text-secondary">{row.failure}</td>
												<td className="px-3 py-3">
													<div className="text-text-secondary">{row.rerun}</div>
													<a
														href={buildSingleStageRuntimeHref(row)}
														className="mt-2 inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
													>
														{row.actionLabel}
														<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
													</a>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="grid gap-3 lg:grid-cols-[1fr_1fr]">
								<div className="rounded-md border border-surface-border bg-surface-background p-3 text-sm text-text-secondary">
									<div className="font-medium text-text-primary">当前完成</div>
									<ul className="mt-2 list-disc space-y-1 pl-5 leading-6">
										<li>三类结果表已经能跳到同一处单环节运行入口。</li>
										<li>运行结果以页面会话展示，便于产品/运营现场核对。</li>
										<li>工具环节当前先覆盖 OAuth、SK05 sandbox 和知识检索。</li>
									</ul>
								</div>
								<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
									<div className="font-medium text-text-primary">仍未自动化</div>
									<ul className="mt-2 list-disc space-y-1 pl-5 leading-6">
										<li>人工作业承接已完成；后续继续补评测运行草案和状态预检。</li>
										<li>真实只读继续按接入条件复核，不阻塞首批结果表。</li>
										<li>
											动态工具选择仍需后续增强，不把订单列表、订单详情等工具伪装成已全量接入。
										</li>
									</ul>
								</div>
								<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary lg:col-span-2">
									<div className="font-medium text-text-primary">边界说明</div>
									<p className="mt-2 leading-6">
										当前不自动提交评测运行、不执行真实 Shopify
										写操作、不生成问题样本、不新增接口或数据库、不进入真实写入；
										它只把已有单环节按钮和本次运行结果接回三类结果表口径。
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card
						id="p10-single-stage-manual-candidates"
						data-testid="p10-single-stage-manual-candidate-handoff"
					>
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">单环节人工候选</CardTitle>
									<CardDescription>
										把本次单环节结果带到评测运营，先人工确认是否可复现、是否需要生成问题样本。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-success/30 bg-brand-success/10 px-3 py-2 text-xs font-medium text-brand-success">
									<CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
									人工候选可承接
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_SINGLE_STAGE_MANUAL_CANDIDATE_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1160px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">环节</th>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">结果来源</th>
											<th className="px-3 py-2 font-medium">人工判断</th>
											<th className="px-3 py-2 font-medium">失败类型</th>
											<th className="px-3 py-2 font-medium">责任资产建议</th>
											<th className="px-3 py-2 font-medium">承接入口</th>
										</tr>
									</thead>
									<tbody>
										{P10_SINGLE_STAGE_MANUAL_CANDIDATE_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-single-stage-manual-candidate-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{row.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{row.id}</div>
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{row.sampleId}
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.source}</td>
												<td className="px-3 py-3 text-text-secondary">{row.judge}</td>
												<td className="px-3 py-3 text-text-secondary">{row.failure}</td>
												<td className="px-3 py-3 text-text-secondary">{row.asset}</td>
												<td className="px-3 py-3">
													<a
														href={buildSingleStageManualCandidateHref(row)}
														className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
													>
														进入评测运营承接
														<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
													</a>
													<div className="mt-2 text-xs leading-5 text-text-muted">{row.next}</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="grid gap-3 lg:grid-cols-[1fr_1fr]">
								<div className="rounded-md border border-surface-border bg-surface-background p-3 text-sm text-text-secondary">
									<div className="font-medium text-text-primary">承接规则</div>
									<ul className="mt-2 list-disc space-y-1 pl-5 leading-6">
										<li>先确认本次 actual result 来自链路测试台页面会话。</li>
										<li>再对照 expected 与 actual，判断失败是否可复现。</li>
										<li>最后人工选择 failure_type、responsible_asset 和 next_action。</li>
									</ul>
								</div>
								<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
									<div className="font-medium text-text-primary">边界说明</div>
									<p className="mt-2 leading-6">
										当前不自动提交评测运行、不自动生成问题样本、不写回回归集、不新增接口或数据库；
										它只把单环节结果带到评测运营人工确认，避免把局部误差直接变成责任资产。
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card
						id="p10-single-stage-evalrun-draft-lifecycle"
						data-testid="p10-single-stage-evalrun-draft-lifecycle"
					>
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">评测运行草案与状态</CardTitle>
									<CardDescription>
										承接单环节人工候选，把样本、环节、失败类型和责任资产先整理成评测运行请求草案；当前只做预检展示。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<CircleAlert className="h-3.5 w-3.5" aria-hidden="true" />
									can_submit_eval_run=false
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_SINGLE_STAGE_EVALRUN_DRAFT_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1180px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">环节</th>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">评测运行请求草案</th>
											<th className="px-3 py-2 font-medium">状态生命周期</th>
											<th className="px-3 py-2 font-medium">人工门禁</th>
											<th className="px-3 py-2 font-medium">边界</th>
											<th className="px-3 py-2 font-medium">草案入口</th>
										</tr>
									</thead>
									<tbody>
										{P10_SINGLE_STAGE_EVALRUN_DRAFT_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-single-stage-evalrun-draft-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{row.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{row.id}</div>
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{row.sampleId}
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.request}</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{row.lifecycle}
												</td>
												<td className="px-3 py-3 text-text-secondary">
													<div>{row.gate}</div>
													<div className="mt-1 text-xs text-text-muted">{row.failure}</div>
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.boundary}</td>
												<td className="px-3 py-3">
													<a
														href={buildSingleStageEvalRunDraftHref(row)}
														className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
													>
														预览评测运行草案
														<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
													</a>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
								<div className="font-medium text-text-primary">边界说明</div>
								<p className="mt-2 leading-6">
									当前不新增接口、数据库或接口文档，不自动提交评测运行，不自动生成问题样本，不写回回归集；
									它只是把单环节人工候选变成可审核、可对比的请求草案和状态门。
								</p>
							</div>
						</CardContent>
					</Card>

					<Card
						id="p10-single-stage-submit-readiness-gate"
						data-testid="p10-single-stage-submit-readiness-gate"
					>
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">提交前检查</CardTitle>
									<CardDescription>
										承接评测运行草案，先判断每个单环节草案能否进入 submit-ready
										候选；当前仍不触发真实提交。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
									不调用评测运行提交接口
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_SINGLE_STAGE_SUBMIT_READINESS_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1180px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">环节</th>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">门禁状态</th>
											<th className="px-3 py-2 font-medium">确认项</th>
											<th className="px-3 py-2 font-medium">允许状态</th>
											<th className="px-3 py-2 font-medium">责任资产</th>
											<th className="px-3 py-2 font-medium">预检入口</th>
										</tr>
									</thead>
									<tbody>
										{P10_SINGLE_STAGE_SUBMIT_READINESS_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-single-stage-submit-readiness-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{row.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{row.id}</div>
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{row.sampleId}
												</td>
												<td className="px-3 py-3">
													<div className="font-mono text-xs font-medium text-text-primary">
														{row.state}
													</div>
													<div className="mt-1 text-xs leading-5 text-text-muted">
														{row.blocking}
													</div>
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.confirmation}</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{row.transition}
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.asset}</td>
												<td className="px-3 py-3">
													<a
														href={buildSingleStageSubmitReadinessHref(row)}
														className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
													>
														查看 submit-ready 预检
														<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
													</a>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
								<div className="font-medium text-text-primary">边界说明</div>
								<p className="mt-2 leading-6">
									当前不调用评测运行提交接口，不新增接口、数据库或接口文档，不自动生成问题样本，也不写回回归集；它只把
									submit-ready 前的人工确认门和阻塞原因产品化。
								</p>
							</div>
						</CardContent>
					</Card>

					<Card
						id="p10-single-stage-submit-contract"
						data-testid="p10-single-stage-submit-contract"
					>
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">评测运行提交请求</CardTitle>
									<CardDescription>
										承接提交候选，把可提交的单环节草案整理成评测运行兼容请求；当前仍不触发真实提交。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-warning/30 bg-brand-warning/10 px-3 py-2 text-xs font-medium text-brand-warning">
									<LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
									不调用评测运行提交接口
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_SINGLE_STAGE_SUBMIT_CONTRACT_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1180px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">环节</th>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">提交状态</th>
											<th className="px-3 py-2 font-medium">请求体</th>
											<th className="px-3 py-2 font-medium">审核项</th>
											<th className="px-3 py-2 font-medium">责任资产</th>
											<th className="px-3 py-2 font-medium">预检入口</th>
										</tr>
									</thead>
									<tbody>
										{P10_SINGLE_STAGE_SUBMIT_CONTRACT_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-single-stage-submit-contract-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{row.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{row.id}</div>
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{row.sampleId}
												</td>
												<td className="px-3 py-3">
													<div className="font-mono text-xs font-medium text-text-primary">
														{row.state}
													</div>
													<div className="mt-1 text-xs leading-5 text-text-muted">
														{row.boundary}
													</div>
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.request}</td>
												<td className="px-3 py-3 text-text-secondary">{row.review}</td>
												<td className="px-3 py-3 text-text-secondary">{row.asset}</td>
												<td className="px-3 py-3">
													<a
														href={buildSingleStageSubmitContractHref(row)}
														className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
													>
														查看提交请求预检
														<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
													</a>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
								<div className="font-medium text-text-primary">边界说明</div>
								<p className="mt-2 leading-6">
									当前只准备 EvalRunCreateRequest
									兼容请求，不新增接口、数据库或接口文档，不调用评测运行提交接口，不自动生成问题样本，也不写回回归集；真实提交、状态回写和失败回流仍需后续合同。
								</p>
							</div>
						</CardContent>
					</Card>

					<Card id="p10-single-stage-submit-gate" data-testid="p10-single-stage-submit-gate">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">人工提交执行与回执</CardTitle>
									<CardDescription>
										承接提交请求合同，把可人工提交的单环节请求和现有评测运行
										接口、状态回写、阻塞边界对齐；提交回执 已在评测运营页补人工提交按钮和回执。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-success/30 bg-brand-success/10 px-3 py-2 text-xs font-medium text-brand-success">
									<ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
									人工确认后复用现有接口
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_SINGLE_STAGE_MANUAL_SUBMIT_GATE_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[1180px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">环节</th>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">执行门状态</th>
											<th className="px-3 py-2 font-medium">调用接口</th>
											<th className="px-3 py-2 font-medium">状态回写</th>
											<th className="px-3 py-2 font-medium">提交后检查</th>
											<th className="px-3 py-2 font-medium">执行门入口</th>
										</tr>
									</thead>
									<tbody>
										{P10_SINGLE_STAGE_MANUAL_SUBMIT_GATE_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-single-stage-submit-gate-row"
											>
												<td className="px-3 py-3">
													<div className="font-medium text-text-primary">{row.label}</div>
													<div className="mt-1 font-mono text-xs text-text-muted">{row.id}</div>
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{row.sampleId}
												</td>
												<td className="px-3 py-3">
													<div className="font-mono text-xs font-medium text-text-primary">
														{row.state}
													</div>
													<div className="mt-1 text-xs leading-5 text-text-muted">
														{row.boundary}
													</div>
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{row.endpoint}
												</td>
												<td className="px-3 py-3 font-mono text-xs text-text-secondary">
													{row.transition}
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.check}</td>
												<td className="px-3 py-3">
													<a
														href={buildSingleStageSubmitGateHref(row)}
														className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-background px-2.5 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-subtle"
													>
														查看并人工提交
														<ArrowRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
													</a>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3 text-sm text-text-secondary">
								<div className="font-medium text-text-primary">边界说明</div>
								<p className="mt-2 leading-6">
									当前定义人工确认后如何复用现有评测运行接口；提交回执在评测运营页允许人工点击提交并展示。
									不新增接口、数据库或接口文档，不自动生成问题样本，也不写回回归集；审计事件和失败回流仍按后续切片推进。
								</p>
							</div>
						</CardContent>
					</Card>

					<Card data-testid="p10-tool-audit-write-action-plan">
						<CardHeader>
							<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle className="text-lg">写操作计划规则</CardTitle>
									<CardDescription>
										围绕 P10TA007-P10TA010，把取消、退款、改地址先收成计划、确认、dry-run 和 Q gate
										合同，不执行真实写操作。
									</CardDescription>
								</div>
								<div className="inline-flex w-fit items-center gap-2 rounded-md border border-brand-danger/30 bg-brand-danger/10 px-3 py-2 text-xs font-medium text-brand-danger">
									<LockKeyhole className="h-3.5 w-3.5" aria-hidden="true" />
									真实写入未开启
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{P10_TOOL_AUDIT_WRITE_ACTION_SUMMARY.map((item) => {
									const Icon = item.icon;
									return (
										<div
											key={item.label}
											className="rounded-md border border-surface-border bg-surface-background p-3"
										>
											<div className="flex items-center gap-2 text-xs font-medium text-text-muted">
												<Icon className="h-3.5 w-3.5 text-brand-primary" aria-hidden="true" />
												{item.label}
											</div>
											<div className="mt-2 text-sm font-semibold text-text-primary">
												{item.value}
											</div>
											<p className="mt-1 text-xs leading-5 text-text-muted">{item.helper}</p>
										</div>
									);
								})}
							</div>

							<div className="overflow-x-auto rounded-md border border-surface-border">
								<table className="w-full min-w-[980px] border-collapse text-sm">
									<thead className="bg-surface-subtle text-left text-xs text-text-muted">
										<tr>
											<th className="px-3 py-2 font-medium">样本</th>
											<th className="px-3 py-2 font-medium">业务动作</th>
											<th className="px-3 py-2 font-medium">能力</th>
											<th className="px-3 py-2 font-medium">计划模式</th>
											<th className="px-3 py-2 font-medium">必要条件</th>
											<th className="px-3 py-2 font-medium">保护边界</th>
											<th className="px-3 py-2 font-medium">下一步</th>
										</tr>
									</thead>
									<tbody>
										{P10_TOOL_AUDIT_WRITE_ACTION_ROWS.map((row) => (
											<tr
												key={row.id}
												className="border-t border-surface-border align-top"
												data-testid="p10-tool-audit-write-action-row"
											>
												<td className="px-3 py-3 font-mono text-xs font-semibold text-text-primary">
													{row.id}
												</td>
												<td className="px-3 py-3 text-text-secondary">{row.scenario}</td>
												<td className="px-3 py-3 text-text-secondary">{row.capability}</td>
												<td className="px-3 py-3 text-text-secondary">{row.mode}</td>
												<td className="px-3 py-3 text-text-secondary">{row.required}</td>
												<td className="px-3 py-3 text-text-secondary">{row.guardrail}</td>
												<td className="px-3 py-3 text-text-secondary">{row.nextAction}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="rounded-md border border-surface-border bg-surface-subtle p-3">
								<div className="text-xs font-medium text-text-muted">后续需固化字段</div>
								<div className="mt-2 flex flex-wrap gap-2">
									{P10_TOOL_AUDIT_WRITE_ACTION_FIELDS.map((field) => (
										<span
											key={field}
											className="rounded-md border border-surface-border bg-surface-background px-2.5 py-1 text-xs text-text-secondary"
										>
											{field}
										</span>
									))}
								</div>
								<p className="mt-3 text-xs leading-5 text-text-muted">
									这里先定义写操作计划合同：不执行真实写操作、不新增接口或数据库、不改最终回复链路。
								</p>
							</div>
						</CardContent>
					</Card>
				</div>
			</details>
		</div>
	);
}

function RelationNode({
	title,
	helper,
	tone = "default",
}: {
	title: string;
	helper: string;
	tone?: "default" | "primary";
}) {
	return (
		<div
			className={
				tone === "primary"
					? "rounded-md border border-brand-primary/30 bg-brand-primary/10 p-3"
					: "rounded-md border border-surface-border bg-surface-background p-3"
			}
		>
			<div className="text-sm font-semibold text-text-primary">{title}</div>
			<p className="mt-1 text-xs leading-5 text-text-muted">{helper}</p>
		</div>
	);
}

function RelationArrow() {
	return (
		<div className="hidden items-center justify-center text-text-muted md:flex">
			<ArrowRight className="h-4 w-4" aria-hidden="true" />
		</div>
	);
}

function ChecklistItem({ children }: { children: string }) {
	return (
		<div className="flex gap-2">
			<ClipboardList className="mt-0.5 h-4 w-4 flex-none text-brand-primary" aria-hidden="true" />
			<span>{children}</span>
		</div>
	);
}

function QuickLink({ to, label }: { to: "/pipeline-testbench" | "/eval"; label: string }) {
	return (
		<Link
			to={to}
			className="inline-flex items-center justify-between rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-subtle"
		>
			{label}
			<ArrowRight className="h-3.5 w-3.5 text-text-muted" aria-hidden="true" />
		</Link>
	);
}

function buildToolAuditBenchHref(sample: (typeof P10_TOOL_AUDIT_ROWS)[number]) {
	return `/pipeline-testbench?mock=1&source=p10_tool_audit&sample_id=${sample.id}&expected_intent=${sample.expectedIntent}&expected_pb=${sample.expectedPb}`;
}

function buildContextEvalParams(sample: (typeof P10_CONTEXT_EVAL_ROWS)[number]) {
	const params = new URLSearchParams({
		mock: "1",
		source: "p10_context_eval",
		sample_id: sample.id,
		message: sample.message,
		expected_intent: sample.expectedIntent,
		expected_pb: sample.expectedPb,
		history: JSON.stringify(sample.history),
		previous_intent: sample.previousIntent,
		current_playbook_id: sample.currentPlaybookId,
		target_stage: sample.targetStage,
	});
	return params.toString();
}

function buildContextEvalBenchHref(sample: (typeof P10_CONTEXT_EVAL_ROWS)[number]) {
	return `/pipeline-testbench?${buildContextEvalParams(sample)}#single-stage-diagnostics`;
}

function buildContextEvalStageHref(sample: (typeof P10_CONTEXT_EVAL_ROWS)[number]) {
	return `${sample.stageRoute}?${buildContextEvalParams(sample)}#${sample.stageHash}`;
}

function buildSingleStageRuntimeHref(sample: (typeof P10_SINGLE_STAGE_FIRST_BATCH_ROWS)[number]) {
	const params = new URLSearchParams({
		mock: "1",
		source: "p10_tool_audit",
		sample_id: sample.runtimeSampleId,
		message: sample.runtimeMessage,
		expected_intent: sample.expectedIntent,
		expected_pb: sample.expectedPb,
		target_stage: sample.id,
	});
	return `/pipeline-testbench?${params.toString()}#single-stage-diagnostics`;
}

function buildSingleStageFullMenuCoverageHref(
	row: (typeof P10_SINGLE_STAGE_FULL_MENU_ROWS)[number],
) {
	if ("targetStage" in row && row.targetStage) {
		const params = new URLSearchParams({
			mock: "1",
			source: "p10_tool_audit",
			sample_id: row.sampleId,
			message: row.message,
			expected_intent: row.expectedIntent,
			expected_pb: row.expectedPb,
			target_stage: row.targetStage,
		});
		return `/pipeline-testbench?${params.toString()}#single-stage-diagnostics`;
	}
	return row.href ?? "";
}

function buildSingleStageManualCandidateHref(
	row: (typeof P10_SINGLE_STAGE_MANUAL_CANDIDATE_ROWS)[number],
) {
	const params = new URLSearchParams({
		source: "p10_single_stage",
		sample_id: row.sampleId,
		target_stage: row.id,
		candidate_mode: "manual",
	});
	return `/eval?${params.toString()}#single-stage-candidate`;
}

function buildSingleStageEvalRunDraftHref(
	row: (typeof P10_SINGLE_STAGE_EVALRUN_DRAFT_ROWS)[number],
) {
	const params = new URLSearchParams({
		source: "p10_single_stage",
		sample_id: row.sampleId,
		target_stage: row.id,
		candidate_mode: "manual",
		draft_mode: "eval_run",
	});
	return `/eval?${params.toString()}#single-stage-evalrun-draft`;
}

function buildSingleStageSubmitReadinessHref(
	row: (typeof P10_SINGLE_STAGE_SUBMIT_READINESS_ROWS)[number],
) {
	const params = new URLSearchParams({
		source: "p10_single_stage",
		sample_id: row.sampleId,
		target_stage: row.id,
		candidate_mode: "manual",
		draft_mode: "eval_run",
		readiness_mode: "submit_ready",
	});
	return `/eval?${params.toString()}#single-stage-submit-readiness`;
}

function buildSingleStageSubmitContractHref(
	row: (typeof P10_SINGLE_STAGE_SUBMIT_CONTRACT_ROWS)[number],
) {
	const params = new URLSearchParams({
		source: "p10_single_stage",
		sample_id: row.sampleId,
		target_stage: row.id,
		candidate_mode: "manual",
		draft_mode: "eval_run",
		readiness_mode: "submit_ready",
		submit_mode: "eval_run_request",
	});
	return `/eval?${params.toString()}#single-stage-submit-contract`;
}

function buildSingleStageSubmitGateHref(
	row: (typeof P10_SINGLE_STAGE_MANUAL_SUBMIT_GATE_ROWS)[number],
) {
	const params = new URLSearchParams({
		source: "p10_single_stage",
		sample_id: row.sampleId,
		target_stage: row.id,
		candidate_mode: "manual",
		draft_mode: "eval_run",
		readiness_mode: "submit_ready",
		submit_mode: "eval_run_request",
		execution_mode: "manual_submit_gate",
	});
	return `/eval?${params.toString()}#single-stage-submit-gate`;
}

function statusPillClass(status: string) {
	if (status === "通过") {
		return "inline-flex rounded-md border border-brand-success/30 bg-brand-success/10 px-2 py-1 text-xs font-medium text-brand-success";
	}
	if (status === "阻塞") {
		return "inline-flex rounded-md border border-brand-warning/30 bg-brand-warning/10 px-2 py-1 text-xs font-medium text-brand-warning";
	}
	return "inline-flex rounded-md border border-surface-border bg-surface-subtle px-2 py-1 text-xs font-medium text-text-secondary";
}

const testDatasetsHelp: PageHelp = {
	title: "测试集说明",
	description: "这里是评测运营的数据资产视图，说明每组样本覆盖什么、怎么跑、失败后怎么沉淀。",
	sections: [
		{
			title: "怎么用",
			items: [
				"上线前先看本次改动涉及哪些测试集，再回到评测运营运行。",
				"单条样本失败时，进入链路测试台看具体失败节点。",
				"新增功能至少补一条正常样本和一条边界样本。",
			],
		},
		{
			title: "当前边界",
			items: [
				"第一版先把 Golden15、S2 基础回归和真实边界样本可视化。",
				"工具审计候选集先做页面投影和链路测试入口，不新增接口或数据库。",
				"当前只做评测运行输入子集转换和不可运行原因展示，不自动发起评测。",
				"当前只生成评测运行发起规则和数据模式拆分说明，不替用户自动提交评测。",
				"当前只独立展示模拟、只读和试运行套件规则，不新增评测运行调度或真实读写。",
				"当前只展示显式提交和复跑验收门，不自动提交、不自动复跑、不自动写回。",
				"当前只展示写操作保护样本的运行记录判分和人工确认规则，不读取真实运行记录、不执行真实写操作。",
				"当前只展示真实运行记录字段合同，不读取真实运行记录、不改最终回复链路。",
				"当前只展示自主只读验证合同，不执行真实自主工具循环、不执行真实写操作。",
				"当前只展示写操作计划、真实只读字段、准入门、运行规则、实现前清单、用户动作接收单和有限验证候选，不执行真实读写、不提交评测运行。",
				"当前只展示上下文专项样本和深链验收入口，不新增长期记忆、不新增接口或数据库。",
				"在线新增、审核和持久化留给 P3-B，不在本切片扩大数据库范围。",
			],
		},
	],
};
